import React, { useState, useEffect, useRef } from 'react';
import {
	ChevronDownIcon,
	BriefcaseIcon,
	PlusIcon,
	MinusIcon,
	TrashIcon,
	ArchiveBoxIcon,
	CheckCircleIcon,
	DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import SearchFilters2 from '../filters/SearchFilters2';
import RichTextEditor from '../common/RichTextEditor';
import { FiltersState, defaultFiltersState } from '../../hooks/useFilters';
import {
	getCampaigns,
	getCampaignById,
	getCampaignStats,
	getCampaignCandidates,
	archiveCampaign,
	deleteCampaign,
	stopCampaign,
	startCampaign,
	createCampaign,
	updateCampaign,
	updateFirstMessageTemplate,
	getAccountTemplate,
	saveAccountTemplate,
	getPositionTemplate,
	savePositionTemplate,
	openCandidateCommunicationMethod,
	retryCampaignCandidateMessage
} from '../../services/aiRecruiterService';
import api, { getHHActiveVacancies } from '../../relevanter/services/api';
import { useAuthStore } from '../../store/authStore';
import type { Campaign, CampaignStats, SalaryData, CustomPrompts, Candidate, CandidateCommunicationMethod } from '../../types/aiRecruiter';
import toast from 'react-hot-toast';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { useVacancyStore } from '../../store/vacancyStore';
import { apiService } from '../../services/apiService';
import { mapErrorCodeToLabel } from './utils/errorLabel';
import { formatCandidateShortName } from '../../relevanter/utils/candidatePrivacy';
import EStaffResumeModal from '../../relevanter/components/EStaffResumeModal';
import avitoIcon from '../../assets/icons/channels/avito.svg';
import maxIcon from '../../assets/icons/channels/max.svg';
import {
	DEFAULT_MESSENGER_PRIORITY,
	getAllowedMessengersForSource,
	getCampaignSourceKind,
	normalizeMessengerPriorityForSource,
	parseMaybeObject,
} from './vacanciesSourceSettings';

const avitoIconSrc = typeof avitoIcon === 'string' ? avitoIcon : '';
const maxIconSrc = typeof maxIcon === 'string' ? maxIcon : '';

const AVITO_VACANCY_LINK_REQUIRED_MESSAGE = 'К выбранной вакансии не привязана вакансия Avito. Сначала привяжите вакансию Avito в карточке вакансии.';
const AVITO_VACANCY_CONFIRM_REQUIRED_MESSAGE = 'Подтвердите привязанную вакансию Avito для отправки через Avito.';

const normalizeOptionalId = (value: unknown): string => {
	const normalized = String(value ?? '').trim();
	return normalized || '';
};

const getLinkedAvitoVacancyId = (value: unknown): string => {
	const data = parseMaybeObject(value) || {};
	const directId = normalizeOptionalId(data.avitoVacancyId);
	if (directId) return directId;

	const sourceVacancyIds = parseMaybeObject(data.sourceVacancyIds);
	return normalizeOptionalId(sourceVacancyIds?.AVITO ?? sourceVacancyIds?.avito);
};

interface VacanciesTabProps {
	selectedCampaign: string | null;
	setSelectedCampaign: (campaignId: string | null) => void;
	onSave?: React.MutableRefObject<(() => Promise<boolean>) | null>;
	onValidateStart?: React.MutableRefObject<(() => boolean) | null>;
	/** When true, removes the outer padding so the content aligns with the host form's gutter. */
	embedded?: boolean;
}

interface ConfirmDialogState {
	isOpen: boolean;
	title: string;
	message: string;
	onConfirm: () => void;
}

interface RetryCandidateDialogState {
	isOpen: boolean;
	candidate: Candidate | null;
	isSubmitting: boolean;
}

type FirstMessageTemplateTab = 'cold' | 'clarify';

const DEFAULT_COLD_FIRST_MESSAGE_TEMPLATE = 'Добрый день, {{candidate_name}}! Я ИИ рекрутер компании {{company_name}}. Я работаю в паре с реальным рекрутером {{recruiter_name}}. Хотим предложить Вам вакансию {{position}}. Думаю, она может Вас заинтересовать.';

const DEFAULT_CLARIFY_FIRST_MESSAGE_TEMPLATE = `{{candidate_name}}, здравствуйте!

Спасибо Вам за отклик на вакансию {{position}}!

Ваше резюме показалось нам очень интересным, но мы хотели бы уточнить у вас опыт по важным для нас требованиям.

Вы не против ответить на вопросы нашего ИИ помощника?

Отвечайте, пожалуйста, на вопросы подробно, хотя бы 2-3 предложения. Ответы "Да, работал" или "Опыт 5 лет" не позволят нам полноценно оценить ваш опыт.

Спасибо!
{{recruiter_name}}`;

const DEFAULT_APPROVAL_TEMPLATE = '{{candidate_name}}, ваше резюме было одобрено. Рекрутер свяжется с вами в ближайшее время.';

const DEFAULT_REJECTION_TEMPLATE = '{{candidate_name}}, к сожалению, по вашему резюме пришел отказ. Но мы в любом случае остаемся на связи, если будет что-то более подходящее, я вам сразу напишу!';

interface FormDataState {
	campaignName: string;
	dialogs: {
		allDialogs: number;
		sutki: number;
	};
	responseDelay: {
		min: number;
		max: number;
	};
	initiationAttempts: number;
	vacancy: {
		position: string;
		salaryEnabled: boolean;
		salaryTo: string;
		currency: string;
		salaryType: string;
		premium: boolean;
		highlightSalary: boolean;
		projectDescription: string;
		companyBenefits: string;
	};
	workType: string;
	workingDay: string;
	benefits: Array<{ text: string; checked: boolean }>;
	relocationCity: string;
	employmentOptions: string;
	recruiterName: string;
	employmentTypes: {
		tkRf: boolean;
		gph: boolean;
		ip: boolean;
		selfEmployed: boolean;
	};
	isAccreditedIT: boolean;
	hhVacancyId: string;
	avitoVacancyId: string;
}

const CHANNEL_META = {
	TELEGRAM: {
		label: 'Telegram',
		className: 'text-blue-500 hover:text-blue-700',
	},
	WHATSAPP: {
		label: 'WhatsApp',
		className: 'text-green-600 hover:text-green-700',
	},
	HH: {
		label: 'HeadHunter',
		className: 'text-red-600 hover:text-red-700',
	},
	MAX: {
		label: 'Max',
		className: 'text-blue-600 hover:text-blue-700',
	},
	AVITO: {
		label: 'Авито',
		className: 'text-green-600 hover:text-green-700',
	},
} as const;

const isShortCandidateName = (value?: string | null): boolean => {
	const parts = value?.trim().split(/\s+/).filter(Boolean) || [];
	return parts.length === 2 && Array.from(parts[1]).length <= 2;
};

const getCandidateDisplayName = (candidate: Candidate): string => {
	if (candidate.pdnConsent) {
		return candidate.fullName || candidate.name || candidate.displayName || formatCandidateShortName(candidate);
	}
	const apiDisplayName = candidate.displayName || candidate.name;
	if (isShortCandidateName(apiDisplayName)) {
		return apiDisplayName!.trim();
	}
	return formatCandidateShortName(candidate, candidate.name || candidate.displayName || 'Кандидат');
};

const getCandidateCommunicationMethods = (candidate: Candidate) => {
	return Array.isArray(candidate.communicationMethods)
		? candidate.communicationMethods.filter((method) => method?.type && method?.type !== 'HH' && method?.url)
		: [];
};

const ChannelIcon = ({ type }: { type: keyof typeof CHANNEL_META }) => {
	if (type === 'TELEGRAM') {
		return (
			<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
			</svg>
		);
	}
	if (type === 'WHATSAPP') {
		return (
			<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
				<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
			</svg>
		);
	}
	if (type === 'AVITO') {
		return avitoIconSrc
			? <img src={avitoIconSrc} alt="" className="h-4 w-4" aria-hidden="true" />
			: <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-green-600 text-[9px] font-bold text-white" aria-hidden="true">A</span>;
	}
	if (type === 'MAX') {
		return maxIconSrc
			? <img src={maxIconSrc} alt="" className="h-4 w-4" aria-hidden="true" />
			: <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-blue-600 text-[9px] font-bold text-white" aria-hidden="true">M</span>;
	}
	return (
		<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white flex-shrink-0" aria-hidden="true">
			hh
		</span>
	);
};

const getCandidateDialogueMessenger = (candidate: Candidate): keyof typeof CHANNEL_META | null => {
	const messenger = String(candidate.messengerType || '').toUpperCase();
	if (messenger === 'TELEGRAM' || messenger === 'WHATSAPP' || messenger === 'MAX' || messenger === 'HH' || messenger === 'AVITO') {
		return messenger;
	}
	return null;
};

type EStaffResumeData = {
	id: string;
	source: 'estaff';
};

type CandidateResumeAction =
	| { type: 'link'; url: string; label: 'hh.ru' | 'Avito'; className: string; title: string }
	| { type: 'estaff'; data: EStaffResumeData };

const normalizeCandidateActionId = (value: unknown): string | null => {
	if (value == null) return null;
	const normalized = String(value).trim();
	return normalized || null;
};

const getCandidateSourceValue = (candidate: Candidate): string => (
	String(candidate.source || '').trim().toUpperCase()
);

const isAvitoCandidateSource = (candidate: Candidate): boolean => {
	const source = getCandidateSourceValue(candidate);
	return source === 'AVITO' || source === 'AVITO_RESPONSE' || source === 'AVITO_SEARCH';
};

const isEStaffCandidateSource = (candidate: Candidate): boolean => getCandidateSourceValue(candidate) === 'ESTAFF';

const isAvitoResumeUrl = (value?: string | null): boolean => {
	const trimmed = String(value || '').trim();
	if (!trimmed) return false;
	try {
		const url = new URL(trimmed);
		const hostname = url.hostname.toLowerCase();
		return hostname === 'avito.ru' || hostname.endsWith('.avito.ru');
	} catch {
		return false;
	}
};

const getCandidateResumeAction = (candidate: Candidate): CandidateResumeAction | null => {
	if (isEStaffCandidateSource(candidate)) {
		const id = normalizeCandidateActionId(candidate.candidateId) || normalizeCandidateActionId(candidate.externalId);
		return id ? { type: 'estaff', data: { id, source: 'estaff' } } : null;
	}

	const resumeUrl = typeof candidate.resumeUrl === 'string' ? candidate.resumeUrl.trim() : '';
	if (!resumeUrl) return null;

	if (isAvitoCandidateSource(candidate) || isAvitoResumeUrl(resumeUrl)) {
		return {
			type: 'link',
			url: resumeUrl,
			label: 'Avito',
			title: 'Открыть резюме на Авито',
			className: 'bg-green-600 hover:bg-green-700 text-white',
		};
	}

	return {
		type: 'link',
		url: resumeUrl,
		label: 'hh.ru',
		title: 'Открыть резюме на hh.ru',
		className: 'bg-red-600 hover:bg-red-700 text-white',
	};
};

interface MessengerAccount {
	id: string;
	accountName?: string;
	phoneNumber?: string;
	messengerType: string;
	authStatus: string;
	isActive?: boolean;
	clientActive?: boolean;
}

const isSendReadyMessengerAccount = (account: MessengerAccount): boolean =>
	account.isActive !== false &&
	['AUTHENTICATED', 'ACTIVE'].includes(account.authStatus) &&
	(account.messengerType !== 'TELEGRAM' || account.clientActive !== false);

const resolveSavedAccountId = (savedAccountId: unknown, accounts: MessengerAccount[]): string => {
	if (!savedAccountId) return '';
	const saved = String(savedAccountId);
	return accounts.some((account) => String(account.id) === saved) ? saved : '';
};

const compareCampaignsNewestFirst = (a: Campaign, b: Campaign): number => {
	const versionDiff = Number(b.version || 0) - Number(a.version || 0);
	if (versionDiff !== 0) return versionDiff;
	return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
};

const getCandidateUniqueKey = (candidate: Candidate): string => {
	const stableKey = candidate.hhResumeId
		|| candidate.candidateId
		|| candidate.sourceExternalId
		|| candidate.externalId
		|| candidate.email
		|| candidate.phone;
	const nameKey = normalizeCandidateIdentityText(
		candidate.fullName
		|| candidate.name
		|| candidate.displayName
		|| [candidate.lastName, candidate.firstName, candidate.middleName].filter(Boolean).join(' ')
	);
	return nameKey || (stableKey ? String(stableKey) : `row:${candidate.id}`);
};

const normalizeCandidateIdentityText = (value?: string | null): string | null => {
	if (!value) return null;
	const normalized = value
		.normalize('NFKC')
		.replace(/[Aa]/g, 'а')
		.replace(/[Ee]/g, 'е')
		.replace(/[Oo]/g, 'о')
		.replace(/[Pp]/g, 'р')
		.replace(/[Cc]/g, 'с')
		.replace(/[Kk]/g, 'к')
		.replace(/[Xx]/g, 'х')
		.replace(/[Yy]/g, 'у')
		.replace(/\s+/g, ' ')
		.trim()
		.toLocaleLowerCase('ru-RU');
	return normalized || null;
};

const dedupeCandidatesByPerson = (candidates: Candidate[]): Candidate[] => {
	const uniqueCandidates = new Map<string, Candidate>();
	candidates.forEach((candidate) => {
		const key = getCandidateUniqueKey(candidate);
		if (!uniqueCandidates.has(key)) {
			uniqueCandidates.set(key, candidate);
		}
	});
	return Array.from(uniqueCandidates.values());
};

const VacanciesTab: React.FC<VacanciesTabProps> = ({ selectedCampaign, setSelectedCampaign, onSave, onValidateStart, embedded = false }) => {
	const authUser = useAuthStore((state) => state.user);
	const defaultRecruiterName = authUser?.firstName || '';
	const [vacancies, setVacancies] = useState<Campaign[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedCampaignData, setSelectedCampaignData] = useState<Campaign | null>(null);
	// Ref to keep latest selectedCampaignData for closure in handleSaveCampaign
	const selectedCampaignDataRef = useRef<Campaign | null>(null);
	const [campaignStats, setCampaignStats] = useState<CampaignStats | null>(null);
	const [showArchived, setShowArchived] = useState(false);
	const [isMessengerPriorityExpanded, setIsMessengerPriorityExpanded] = useState(false);
	const [isLaunching, setIsLaunching] = useState(false);
	const [candidates, setCandidates] = useState<Candidate[]>([]);
	const [loadingCandidates, setLoadingCandidates] = useState(false);
	const [firstMessageTemplate, setFirstMessageTemplate] = useState('');
	const [firstMessageTemplateOriginal, setFirstMessageTemplateOriginal] = useState('');
	const [firstMessageTemplateClarify, setFirstMessageTemplateClarify] = useState('');
	const [firstMessageTemplateClarifyOriginal, setFirstMessageTemplateClarifyOriginal] = useState('');
	const [firstMessageTemplateTab, setFirstMessageTemplateTab] = useState<FirstMessageTemplateTab>('cold');
	const [approvalTemplate, setApprovalTemplate] = useState('');
	const [approvalTemplateOriginal, setApprovalTemplateOriginal] = useState('');
	const [rejectionTemplate, setRejectionTemplate] = useState('');
	const [rejectionTemplateOriginal, setRejectionTemplateOriginal] = useState('');
	const [savingTemplate, setSavingTemplate] = useState(false);
	const [accountTemplate, setAccountTemplate] = useState('');
	const [positionTemplate, setPositionTemplate] = useState('');
	const [accountClarifyTemplate, setAccountClarifyTemplate] = useState('');
	const [positionClarifyTemplate, setPositionClarifyTemplate] = useState('');
	const [accountApprovalTemplate, setAccountApprovalTemplate] = useState('');
	const [accountRejectionTemplate, setAccountRejectionTemplate] = useState('');
	const [positionApprovalTemplate, setPositionApprovalTemplate] = useState('');
	const [positionRejectionTemplate, setPositionRejectionTemplate] = useState('');
	const [savingAccountTemplate, setSavingAccountTemplate] = useState(false);
	const templateTextareaRef = useRef<HTMLTextAreaElement>(null);
	// Flat editable list of requirements used by the dialog agent (ai-recruiter). Stored on the
	// position under `aiRequirementsWeighted.for_dialog` as plain strings — weights are
	// irrelevant for the dialog, they only matter for scoring (relevanter uses required+optional).
	type FlatReq = { id: string; text: string; mandatory: boolean };
	const [candidateRequirements, setCandidateRequirements] = useState<FlatReq[]>([]);
	const [candidateRequirementsOriginal, setCandidateRequirementsOriginal] = useState<FlatReq[]>([]);
	const [savingRequirements, setSavingRequirements] = useState(false);
	const [loadingRequirements, setLoadingRequirements] = useState(false);
	const [showAddReqMenu, setShowAddReqMenu] = useState(false);
	const addReqButtonRef = useRef<HTMLButtonElement>(null);
	const addReqMenuRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!showAddReqMenu) return;
		const onMouseDown = (e: MouseEvent) => {
			const t = e.target as Node;
			if (addReqButtonRef.current?.contains(t) || addReqMenuRef.current?.contains(t)) return;
			setShowAddReqMenu(false);
		};
		document.addEventListener('mousedown', onMouseDown);
		return () => document.removeEventListener('mousedown', onMouseDown);
	}, [showAddReqMenu]);
	// Cache of the full aiRequirementsWeighted object so save preserves required/optional
	// (managed on the vacancy page) and only mutates for_dialog.
	const fullRequirementsJsonRef = useRef<any>(null);
	// Tracks requirement rows currently playing the fade-out animation. After the CSS
	// transition finishes (~200ms), the row is actually removed from state.
	const [removingReqIds, setRemovingReqIds] = useState<Set<string>>(new Set());
	// Which requirement row is in edit mode. Only one row is editable at a time —
	// clicking the pencil on another row implicitly exits the previous edit.
	const [editingReqId, setEditingReqId] = useState<string | null>(null);
	// Per-row textarea refs keyed by requirement id — used by the pencil icon to programmatically
	// focus the field and place the caret at the end.
	const reqTextareaRefs = useRef<Record<string, HTMLTextAreaElement>>({});
	const colors = useBrandingColors();
	const { selectedVacancyId, selectedVacancy, refreshSelectedVacancy } = useVacancyStore();

	// Confirmation dialog state
	const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
		isOpen: false,
		title: '',
		message: '',
		onConfirm: () => {}
	});

	// Save dialog state
	const [saveDialog, setSaveDialog] = useState<{
		isOpen: boolean;
		status: 'loading' | 'success' | 'error';
		message: string;
	}>({
		isOpen: false,
		status: 'loading',
		message: ''
	});
	const [retryCandidateDialog, setRetryCandidateDialog] = useState<RetryCandidateDialogState>({
		isOpen: false,
		candidate: null,
		isSubmitting: false
	});
	const [estaffResumeModalOpen, setEstaffResumeModalOpen] = useState(false);
	const [selectedEStaffResume, setSelectedEStaffResume] = useState<EStaffResumeData | null>(null);

	// Search filters state (for LEFT sidebar)
	const [searchFilters, setSearchFilters] = useState<FiltersState>(defaultFiltersState);

	// Vacancy form data state (for CENTER content)
	const [formData, setFormData] = useState<FormDataState>({
		campaignName: '',
		dialogs: {
			allDialogs: 10,
			sutki: 10
		},
		responseDelay: {
			min: 15,
			max: 25
		},
		initiationAttempts: 2,
		vacancy: {
			position: '',
			salaryEnabled: false,
			salaryTo: '',
			currency: 'RUB',
			salaryType: 'На руки',
			premium: false,
			highlightSalary: false,
			projectDescription: '',
			companyBenefits: ''
		},
		workType: 'Гибрид',
		workingDay: 'Полный день',
		benefits: [
			{
				text: 'Мы предоставим тебе возможность развиваться. Оплатим внешние курсы, дадим доступ к неограниченному количеству обучающих материалов. Ты получишь возможность выступать на конференциях, проводить обучения (при твоем желании, конечно), участвовать в митапах и вебинарах. Хотим развивать экспертность каждого сотрудника.',
				checked: true
			},
			{
				text: 'Мы считаем, что наряду с развитием интеллектуальным, здорово, когда человек уделяет время и физической форме, поэтому предоставляем возможность занятия спортом вместе с компанией. У нас есть собственные спортивные и киберспортивные команды!:) Будем рады видеть тебя в одной из них.',
				checked: true
			},
			{
				text: 'Можно с уверенностью сказать, что наша компания является уникальной благодаря тому, что ставит работу своих сотрудников в приоритет, команда считается основной ценностью для нас. У нас есть Служба заботы, которая поможет тебе решить не только рабочие, но и все личные проблемы.',
				checked: true
			},
			{
				text: 'Индивидуальный соц. пакет. Наша Служба заботы поможет тебе получить бенефиты, которые актуальны именно для тебя.',
				checked: true
			}
		],
		relocationCity: '',
		employmentOptions: '',
		recruiterName: defaultRecruiterName,
		employmentTypes: {
			tkRf: true,
			gph: true,
			ip: true,
			selfEmployed: true
		},
		isAccreditedIT: true,
		hhVacancyId: '',
		avitoVacancyId: ''
	});

	const [expandedBenefits, setExpandedBenefits] = useState(true);
	const [telegramAccounts, setTelegramAccounts] = useState<MessengerAccount[]>([]);
	const [selectedTelegramAccount, setSelectedTelegramAccount] = useState('');
	const [whatsappAccounts, setWhatsappAccounts] = useState<MessengerAccount[]>([]);
	const [selectedWhatsappAccount, setSelectedWhatsappAccount] = useState('');
	const [maxAccounts, setMaxAccounts] = useState<MessengerAccount[]>([]);
	const [selectedMaxAccount, setSelectedMaxAccount] = useState('');
	const [avitoEnabled, setAvitoEnabled] = useState(false);
	const [messengerPriority, setMessengerPriority] = useState<string[]>(() => [...DEFAULT_MESSENGER_PRIORITY]);
	const [contactWindow, setContactWindow] = useState({ start: 10, end: 19 });
	const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
	const [hhVacancies, setHhVacancies] = useState<Array<{ id: string; name: string; area?: { name: string } }>>([]);
	const [hhVacanciesLoading, setHhVacanciesLoading] = useState(false);
	const [hhVacancyDropdownOpen, setHhVacancyDropdownOpen] = useState(false);
	const hhVacancyDropdownRef = useRef<HTMLDivElement>(null);
	const selectedCampaignSource = React.useMemo(
		() => getCampaignSourceKind(selectedCampaignData),
		[selectedCampaignData]
	);
	const selectedAllowsHh = selectedCampaignSource !== 'avito';
	const selectedAllowsAvito = selectedCampaignSource !== 'hh';
	const visibleMessengerPriority = React.useMemo(
		() => normalizeMessengerPriorityForSource(messengerPriority, selectedCampaignSource),
		[messengerPriority, selectedCampaignSource]
	);
	const linkedAvitoVacancyId = getLinkedAvitoVacancyId(selectedVacancy);
	const displayedAvitoVacancyId = linkedAvitoVacancyId || formData.avitoVacancyId || getLinkedAvitoVacancyId(selectedCampaignData);
	const displayedAvitoVacancyTitle =
		normalizeOptionalId((selectedVacancy as any)?.title)
		|| normalizeOptionalId(formData.vacancy.position)
		|| normalizeOptionalId(selectedCampaignData?.targetPosition)
		|| 'выбранная вакансия';

	// Refs and state for dropdown widths
	const telegramTriggerRef = useRef<HTMLButtonElement>(null);
	const whatsappTriggerRef = useRef<HTMLButtonElement>(null);
	const maxTriggerRef = useRef<HTMLButtonElement>(null);
	const [telegramTriggerWidth, setTelegramTriggerWidth] = useState<number | undefined>();
	const [whatsappTriggerWidth, setWhatsappTriggerWidth] = useState<number | undefined>();
	const [maxTriggerWidth, setMaxTriggerWidth] = useState<number | undefined>();

	// Load campaigns on mount and when archive toggle or vacancy changes
	useEffect(() => {
		if (selectedVacancyId) {
			loadCampaigns();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showArchived, selectedVacancyId]);

	// Keep ref in sync with state for closure access
	useEffect(() => {
		selectedCampaignDataRef.current = selectedCampaignData;
	}, [selectedCampaignData]);

	useEffect(() => {
		setMessengerPriority((prev) => normalizeMessengerPriorityForSource(prev, selectedCampaignSource));
		if (!selectedAllowsAvito) {
			setAvitoEnabled(false);
			setFormData((prev) => prev.avitoVacancyId ? { ...prev, avitoVacancyId: '' } : prev);
		}
		if (!selectedAllowsHh) {
			setHhVacancies([]);
			setHhVacancyDropdownOpen(false);
			setFormData((prev) => prev.hhVacancyId ? { ...prev, hhVacancyId: '' } : prev);
		}
	}, [selectedCampaignSource, selectedAllowsAvito, selectedAllowsHh]);

	// Load campaign details when selection changes
	useEffect(() => {
		if (selectedCampaign) {
			loadCampaignDetails(selectedCampaign);
			loadCampaignStatsData(selectedCampaign);
			loadCampaignCandidatesData(selectedCampaign, selectedVacancyId ? Number(selectedVacancyId) : undefined);
		} else if (selectedVacancyId) {
			loadCampaignCandidatesData('', Number(selectedVacancyId));
		} else {
			setCandidates([]);
		}
	}, [selectedCampaign, selectedVacancyId]);

	// Load dialog-requirements list strictly from Position.aiRequirementsWeighted.for_dialog.
	// for_dialog is seeded automatically whenever the AI generates requirements (chat
	// assistant or "AI" button) and on the first save from the vacancy page — no fallback
	// to required/optional here. If a position was created before this feature, the list
	// will be empty until a new generation/save writes for_dialog.
	useEffect(() => {
		const posId = selectedCampaignData?.positionId || selectedVacancyId;
		if (!posId) {
			setCandidateRequirements([]);
			setCandidateRequirementsOriginal([]);
			fullRequirementsJsonRef.current = null;
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				// Reuse already-loaded vacancy from the store if it matches — avoids an extra
				// round-trip through the heavy getPosition endpoint (it also computes avg
				// interview score, slow on DEV).
				const storeVacancy = selectedVacancy && String((selectedVacancy as any).id) === String(posId) ? selectedVacancy : null;
				if (!storeVacancy) setLoadingRequirements(true);
				const position = storeVacancy || await apiService.getPosition(posId);
				const raw = (position as any)?.aiRequirementsWeighted;
				const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
				fullRequirementsJsonRef.current = parsed && typeof parsed === 'object' ? parsed : {};
				// for_dialog может быть в двух форматах:
				//  Новый: {required: [...], optional: [...]} — деление берётся напрямую
				//  Старый плоский массив строк — деление восстанавливаем сопоставлением
				//    текста с оригинальными списками aiRequirementsWeighted.required/optional
				//    (которые редактируются на странице вакансии и хранят разбивку с весами).
				//    Если текст совпадает с optional — считаем дополнительным, иначе обязательным.
				const fd = parsed?.for_dialog;
				const extractTexts = (arr: any): string[] =>
					(Array.isArray(arr) ? arr : [])
						.map((r: any) => (typeof r === 'string' ? r : r?.text))
						.filter((t: any): t is string => typeof t === 'string' && t.trim().length > 0)
						.map((t: string) => t.trim());
				let mandatoryTexts: string[] = [];
				let optionalTexts: string[] = [];
				if (fd && !Array.isArray(fd) && typeof fd === 'object') {
					mandatoryTexts = extractTexts(fd.required);
					optionalTexts = extractTexts(fd.optional);
				} else {
					const flat = extractTexts(fd);
					const optionalSet = new Set(extractTexts(parsed?.optional).map(t => t.toLowerCase()));
					for (const text of flat) {
						if (optionalSet.has(text.toLowerCase())) optionalTexts.push(text);
						else mandatoryTexts.push(text);
					}
				}
				const ts = Date.now();
				const list: FlatReq[] = [
					...mandatoryTexts.map((text, i) => ({ id: `dlg-m-${i}-${ts}`, text, mandatory: true })),
					...optionalTexts.map((text, i) => ({ id: `dlg-o-${i}-${ts}`, text, mandatory: false })),
				];
				if (!cancelled) {
					setCandidateRequirements(list);
					setCandidateRequirementsOriginal(list);
					setLoadingRequirements(false);
				}
			} catch (e) {
				console.error('Failed to load dialog requirements:', e); if (!cancelled) setLoadingRequirements(false);
				if (!cancelled) {
					setCandidateRequirements([]);
					setCandidateRequirementsOriginal([]);
					fullRequirementsJsonRef.current = null;
				}
			}
		})();
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCampaignData?.positionId, selectedVacancyId, (selectedVacancy as any)?.aiRequirementsWeighted]);

	// Fetch messenger accounts on mount
	useEffect(() => {
		fetchMessengerAccounts();
	}, []);

	useEffect(() => {
		if (!selectedVacancyId) return;
		const loadAccountTemplate = async () => {
			try {
				const data = await getAccountTemplate();
				setAccountTemplate(data?.templateText || '');
				setAccountClarifyTemplate(data?.clarifyTemplateText || '');
				setAccountApprovalTemplate(data?.approvalTemplateText || '');
				setAccountRejectionTemplate(data?.rejectionTemplateText || '');
			} catch (error) {
				console.error('Failed to load account template:', error);
			}
		};
		loadAccountTemplate();
	}, [selectedVacancyId]);

	useEffect(() => {
		if (selectedCampaign || !selectedVacancyId) return;
		let cancelled = false;
		const loadVacancyTemplates = async () => {
			try {
				const [posTpl, accTpl] = await Promise.all([
					getPositionTemplate(selectedVacancyId).catch(err => {
						console.error('Failed to load position template:', err);
						return null;
					}),
					getAccountTemplate().catch(err => {
						console.error('Failed to load account template:', err);
						return null;
					}),
				]);
				if (cancelled) return;

				const posFirst = posTpl?.templateText || '';
				const posClarify = posTpl?.clarifyTemplateText || '';
				const posApproval = posTpl?.approvalTemplateText || '';
				const posRejection = posTpl?.rejectionTemplateText || '';
				const accFirst = accTpl?.templateText || '';
				const accClarify = accTpl?.clarifyTemplateText || '';
				const accApproval = accTpl?.approvalTemplateText || '';
				const accRejection = accTpl?.rejectionTemplateText || '';
				const resolvedFirst = posFirst || accFirst || DEFAULT_COLD_FIRST_MESSAGE_TEMPLATE;
				const resolvedClarify = posClarify || accClarify || DEFAULT_CLARIFY_FIRST_MESSAGE_TEMPLATE;
				const resolvedApproval = posApproval || accApproval || DEFAULT_APPROVAL_TEMPLATE;
				const resolvedRejection = posRejection || accRejection || DEFAULT_REJECTION_TEMPLATE;

				setPositionTemplate(posFirst);
				setPositionClarifyTemplate(posClarify);
				setPositionApprovalTemplate(posApproval);
				setPositionRejectionTemplate(posRejection);
				setAccountTemplate(accFirst);
				setAccountClarifyTemplate(accClarify);
				setAccountApprovalTemplate(accApproval);
				setAccountRejectionTemplate(accRejection);
				setFirstMessageTemplate(resolvedFirst);
				setFirstMessageTemplateOriginal(resolvedFirst);
				setFirstMessageTemplateClarify(resolvedClarify);
				setFirstMessageTemplateClarifyOriginal(resolvedClarify);
				setApprovalTemplate(resolvedApproval);
				setApprovalTemplateOriginal(resolvedApproval);
				setRejectionTemplate(resolvedRejection);
				setRejectionTemplateOriginal(resolvedRejection);
			} catch (error) {
				console.error('Failed to load vacancy templates:', error);
			}
		};
		loadVacancyTemplates();
		return () => { cancelled = true; };
	}, [selectedCampaign, selectedVacancyId]);


	// Fetch HH vacancies linked to current position
	useEffect(() => {
		const loadHHVacancies = async () => {
			if (!selectedAllowsHh) {
				setHhVacancies([]);
				setHhVacanciesLoading(false);
				setHhVacancyDropdownOpen(false);
				setFormData((prev) => prev.hhVacancyId ? { ...prev, hhVacancyId: '' } : prev);
				return;
			}
			// Collect HH vacancy IDs linked to the current position
			const linkedIds: string[] = [];
			if (selectedVacancy?.hhVacancyId) {
				linkedIds.push(selectedVacancy.hhVacancyId);
			}
			if (selectedVacancy?.hhAdditionalVacancyIds?.length) {
				linkedIds.push(...selectedVacancy.hhAdditionalVacancyIds);
			}
			if (linkedIds.length === 0) {
				setHhVacancies([]);
				return;
			}

			setHhVacanciesLoading(true);
			try {
				const response = await getHHActiveVacancies();
				const items = response?.vacancies?.items || response?.items || [];
				const allVacancies = Array.isArray(items) ? items : [];
				// Filter to only show vacancies linked to this position
				const filtered = allVacancies.filter((v: { id: string }) => linkedIds.includes(v.id));
				setHhVacancies(filtered);

				// Auto-select primary HH vacancy if none chosen yet
				if (!formData.hhVacancyId && selectedVacancy?.hhVacancyId && filtered.some((v: { id: string }) => v.id === selectedVacancy.hhVacancyId)) {
					setFormData((prev) => prev.hhVacancyId ? prev : { ...prev, hhVacancyId: String(selectedVacancy.hhVacancyId) });
				}
			} catch (err) {
				console.error('Error loading HH vacancies:', err);
				setHhVacancies([]);
			} finally {
				setHhVacanciesLoading(false);
			}
		};
		loadHHVacancies();
	}, [selectedAllowsHh, selectedVacancy?.hhVacancyId, selectedVacancy?.hhAdditionalVacancyIds, formData.hhVacancyId]);

	// Close HH vacancy dropdown on click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (hhVacancyDropdownRef.current && !hhVacancyDropdownRef.current.contains(event.target as Node)) {
				setHhVacancyDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Pass save handler to parent
	// Update on every render to ensure parent always has the latest version of handleSaveCampaign
	React.useLayoutEffect(() => {
		if (onSave) {
			onSave.current = handleSaveCampaign;
		}
		return () => {
			if (onSave?.current === handleSaveCampaign) {
				onSave.current = null;
			}
		};
	});

	React.useLayoutEffect(() => {
		if (onValidateStart) {
			onValidateStart.current = validateMessengerSettings;
		}
		return () => {
			if (onValidateStart?.current === validateMessengerSettings) {
				onValidateStart.current = null;
			}
		};
	});

	// Helper: hex color to rgba with alpha for subtle selection background
	const hexToRgba = (hex: string, alpha: number): string => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	};

	const loadCampaigns = async () => {
		setLoading(true);
		try {
			// Раньше selectedVacancyId передавался как X-Tenant-Id, потому что в ai-recruiter
			// исторически tenant_id = position_id. После TASKNEIROKLYUCH-655 X-Tenant-Id
			// прокидывает прокси из JWT companyId, а фильтр по вакансии идёт через
			// отдельный параметр positionId.
			const params = selectedVacancyId ? { positionId: Number(selectedVacancyId) } : {};
			const data = await getCampaigns(params);
			const campaignsList = data.content || [];

			// Filter campaigns based on archive tab state
			const filteredCampaigns = showArchived
				? campaignsList.filter(c => c.status === 'ARCHIVED')
				: campaignsList.filter(c => c.status !== 'ARCHIVED');

			// Сортируем кампании в обратном порядке (новые сначала)
			const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
				// createdAt приходит как массив [год, месяц, день, час, минута, секунда, наносекунды]
				const timeA = a.createdAt && Array.isArray(a.createdAt)
					? new Date(a.createdAt[0], a.createdAt[1] - 1, a.createdAt[2], a.createdAt[3], a.createdAt[4], a.createdAt[5]).getTime()
					: 0;
				const timeB = b.createdAt && Array.isArray(b.createdAt)
					? new Date(b.createdAt[0], b.createdAt[1] - 1, b.createdAt[2], b.createdAt[3], b.createdAt[4], b.createdAt[5]).getTime()
					: 0;
				return timeB - timeA;
			});

			setVacancies(sortedCampaigns);

			// Не меняем selectedCampaign автоматически - он управляется родителем
			// Только сбрасываем данные если текущая кампания больше не существует в ПОЛНОМ списке (не отфильтрованном)
			if (selectedCampaign) {
				const currentStillExists = campaignsList.some(c => String(c.id) === selectedCampaign);
				if (!currentStillExists) {
					setSelectedCampaignData(null);
					setCampaignStats(null);
				}
			}
		} catch (error) {
			console.error('Failed to load campaigns:', error);
			setVacancies([]);
		} finally {
			setLoading(false);
		}
	};

	const loadCampaignDetails = async (campaignId: string) => {
		try {
			// Clear previous account selections first to prevent mixing data from different campaigns
			setSelectedTelegramAccount('');
			setSelectedWhatsappAccount('');
			setSelectedMaxAccount('');
			const accountSnapshot = await fetchMessengerAccounts();
			const availableTelegramAccounts = accountSnapshot.telegram;
			const availableWhatsappAccounts = accountSnapshot.whatsapp;
			const availableMaxAccounts = accountSnapshot.max;

			// tenantId обязателен — после снятия admin-mode в CampaignService.getCampaign
			// запрос без tenantId падает в AccessDenied → INTERNAL_ERROR 500.
			const campaign = await getCampaignById(campaignId);
			if (campaign) {
				setSelectedCampaignData(campaign);
				const parsedSearchFilters = parseMaybeObject((campaign as any).searchFilters);
				const parsedAccountSettings = parseMaybeObject((campaign as any).accountSettings);
				const campaignSourceKind = getCampaignSourceKind(campaign, parsedSearchFilters, parsedAccountSettings);
				const campaignAllowsHh = campaignSourceKind !== 'avito';
				const campaignAllowsAvito = campaignSourceKind !== 'hh';

				// Грузим шаблоны позиции и аккаунта параллельно — нужны для фолбэка:
				// если у кампании поле пустое, показываем value позиции > аккаунта > хардкод-дефолт.
				// Фолбэк делается только на отображении, в БД кампании остаётся null пока юзер не сохранит.
				const positionTemplatePromise = campaign.positionId
					? getPositionTemplate(campaign.positionId).catch(err => {
						console.error('Failed to load position template:', err);
						return null;
					})
					: Promise.resolve(null);

				const accountTemplatePromise = getAccountTemplate().catch(err => {
					console.error('Failed to load account template:', err);
					return null;
				});

				const [posTpl, accTpl] = await Promise.all([positionTemplatePromise, accountTemplatePromise]);

				const posFirst = posTpl?.templateText || '';
				const posClarify = posTpl?.clarifyTemplateText || '';
				const posApproval = posTpl?.approvalTemplateText || '';
				const posRejection = posTpl?.rejectionTemplateText || '';
				const accFirst = accTpl?.templateText || '';
				const accClarify = accTpl?.clarifyTemplateText || '';
				const accApproval = accTpl?.approvalTemplateText || '';
				const accRejection = accTpl?.rejectionTemplateText || '';

				setPositionTemplate(posFirst);
				setPositionClarifyTemplate(posClarify);
				setPositionApprovalTemplate(posApproval);
				setPositionRejectionTemplate(posRejection);
				if (accFirst) setAccountTemplate(accFirst);
				if (accClarify) setAccountClarifyTemplate(accClarify);

				// Фолбэк: значение кампании → позиция → аккаунт → хардкод-дефолт.
				// Для шаблонов решения пустая строка в кампании означает "не отправлять сообщение".
				// Дефолты согласованы с CampaignService (createCampaign / generateFirstMessageTemplate).
				const resolvedFirst = campaign.firstMessageTemplate || posFirst || accFirst || DEFAULT_COLD_FIRST_MESSAGE_TEMPLATE;
				const resolvedClarify = campaign.firstMessageTemplateClarify || posClarify || accClarify || DEFAULT_CLARIFY_FIRST_MESSAGE_TEMPLATE;
				const resolvedApproval = campaign.approvalMessageTemplate != null
					? campaign.approvalMessageTemplate
					: posApproval || accApproval || DEFAULT_APPROVAL_TEMPLATE;
				const resolvedRejection = campaign.rejectionMessageTemplate != null
					? campaign.rejectionMessageTemplate
					: posRejection || accRejection || DEFAULT_REJECTION_TEMPLATE;

				setFirstMessageTemplate(resolvedFirst);
				setFirstMessageTemplateOriginal(resolvedFirst);
				setFirstMessageTemplateClarify(resolvedClarify);
				setFirstMessageTemplateClarifyOriginal(resolvedClarify);
				setApprovalTemplate(resolvedApproval);
				setApprovalTemplateOriginal(resolvedApproval);
				setRejectionTemplate(resolvedRejection);
				setRejectionTemplateOriginal(resolvedRejection);

				// Parse salary data from socialPackage
				let salaryData: SalaryData | null = null;
				if (campaign.socialPackage) {
					try {
						salaryData =
							typeof campaign.socialPackage === 'string'
								? JSON.parse(campaign.socialPackage)
								: campaign.socialPackage;
					} catch (e) {
						console.error('Failed to parse socialPackage:', e);
					}
				}

				// Parse bonus data from customPrompts
				let hasBonuses = false;
				if (campaign.customPrompts) {
					try {
						const prompts: CustomPrompts =
							typeof campaign.customPrompts === 'string'
								? JSON.parse(campaign.customPrompts)
								: campaign.customPrompts;
						hasBonuses = prompts.hasBonuses || false;
					} catch (e) {
						console.error('Failed to parse customPrompts:', e);
					}
				}

				// Update contactWindow state
				if (campaign.contactWindowStart !== undefined && campaign.contactWindowEnd !== undefined) {
					setContactWindow({
						start: campaign.contactWindowStart ?? 10,
						end: campaign.contactWindowEnd ?? 19
					});
				}

				// Update form data with campaign data
				setFormData((prev) => ({
					...prev,
					campaignName: campaign.name || '',
					recruiterName: (campaign.recruiterName && campaign.recruiterName !== 'Рекрутер') ? campaign.recruiterName : (prev.recruiterName || defaultRecruiterName),
					relocationCity: campaign.relocationCountry || '',
					employmentOptions: campaign.relocationSupport || '',
					employmentTypes: {
						tkRf: campaign.employmentTkRf ?? true,
						gph: campaign.employmentGph ?? true,
						ip: campaign.employmentIp ?? true,
						selfEmployed: campaign.employmentSelfEmployed ?? true
					},
					isAccreditedIT: campaign.isAccreditedIT ?? true,
					hhVacancyId: campaignAllowsHh ? ((campaign as any).hhVacancyId || '') : '',
					avitoVacancyId: campaignAllowsAvito ? ((campaign as any).avitoVacancyId || '') : '',
					responseDelay: {
						min: campaign.responseDelayMin ?? 15,
						max: Math.min(60, campaign.responseDelayMax ?? 25)
					},
					initiationAttempts: campaign.attempts ?? 2,
					dialogs: {
						allDialogs: campaign.maxActiveDialogs ?? 10,
						sutki: Math.min(campaign.dailyLimit ?? 10, campaign.maxActiveDialogs ?? 10)
					},
					vacancy: {
						...prev.vacancy,
						position: campaign.targetPosition || prev.vacancy.position,
						projectDescription: campaign.requirements || campaign.description || prev.vacancy.projectDescription,
						companyBenefits: campaign.companyDescription || campaign.teamDescription || prev.vacancy.companyBenefits,
						salaryEnabled: !!campaign.ndaText,
						salaryTo: salaryData?.max ? String(salaryData.max) : '',
						currency: salaryData?.currency || 'RUB',
						salaryType: salaryData?.isGross ? 'До вычета налогов' : 'На руки',
						premium: hasBonuses,
						highlightSalary: salaryData?.showSalary || false
					},
					workType:
						campaign.workFormat === 'REMOTE'
							? 'Удаленно'
							: campaign.workFormat === 'OFFICE'
							? 'Офис'
							: campaign.workFormat === 'HYBRID'
							? 'Гибрид'
							: prev.workType,
					workingDay: prev.workingDay
				}));

				const loadedSearchSource = String((parsedSearchFilters as any)?.source || '').toLowerCase();
				// Load search filters if they exist
				if (parsedSearchFilters) {
					setSearchFilters((prev) => ({
						...prev,
						...parsedSearchFilters
					}));
					if (loadedSearchSource === 'avito' && campaignAllowsAvito) {
						setAvitoEnabled(true);
					}
				}

				// Load account settings if they exist
				if (parsedAccountSettings) {
					try {
						const accountSettings = parsedAccountSettings;

						const savedTelegramAccountId = resolveSavedAccountId(
							accountSettings.telegramAccountId,
							availableTelegramAccounts
						);
						if (savedTelegramAccountId) {
							setSelectedTelegramAccount(savedTelegramAccountId);
						} else if (accountSettings.telegramAccountId) {
							setSelectedTelegramAccount('');
							toast.error('Сохранённый Telegram-аккаунт недоступен. Выберите актуальный аккаунт перед запуском рассылки.');
						} else {
							setSelectedTelegramAccount('');
						}

						const savedWhatsappAccountId = resolveSavedAccountId(
							accountSettings.whatsappAccountId,
							availableWhatsappAccounts
						);
						if (savedWhatsappAccountId) {
							setSelectedWhatsappAccount(savedWhatsappAccountId);
						} else {
							setSelectedWhatsappAccount('');
						}

						const savedMaxAccountId = resolveSavedAccountId(
							accountSettings.maxAccountId,
							availableMaxAccounts
						);
						if (savedMaxAccountId) {
							setSelectedMaxAccount(savedMaxAccountId);
						} else {
							setSelectedMaxAccount('');
						}

						const enabledMessengers = Array.isArray(accountSettings.enabledMessengers)
							? accountSettings.enabledMessengers.map((messenger: unknown) => String(messenger).toUpperCase())
							: [];
						const savedPriority = Array.isArray(accountSettings.messengerPriority)
							? accountSettings.messengerPriority.map((messenger: unknown) => String(messenger).toUpperCase())
							: [];
						setAvitoEnabled(
							campaignAllowsAvito && (
							enabledMessengers.includes('AVITO') ||
							savedPriority.includes('AVITO') ||
							loadedSearchSource === 'avito'
							)
						);
						setMessengerPriority(normalizeMessengerPriorityForSource(accountSettings.messengerPriority, campaignSourceKind));
					} catch (e) {
						console.error('Failed to parse accountSettings:', e);
					}
				} else {
					setSelectedTelegramAccount('');
					setSelectedWhatsappAccount('');
					setSelectedMaxAccount('');
					setAvitoEnabled(campaignSourceKind === 'avito');
					setMessengerPriority(normalizeMessengerPriorityForSource([], campaignSourceKind));
				}
			}
		} catch (error) {
			console.error('Failed to load campaign details:', error);
		}
	};

	const loadCampaignCandidatesData = async (campaignId: string, positionId?: number) => {
		try {
			setLoadingCandidates(true);
			let candidatesData: Candidate[];
			if (positionId) {
				const campaignsResponse = await getCampaigns({ positionId, size: 1000 });
				const positionCampaigns = [...(campaignsResponse.content || [])]
					.sort(compareCampaignsNewestFirst);
				const candidatesByCampaign = await Promise.all(
					positionCampaigns.map(campaign => getCampaignCandidates(campaign.id))
				);
				candidatesData = dedupeCandidatesByPerson(candidatesByCampaign.flat());
			} else {
				candidatesData = await getCampaignCandidates(campaignId);
			}
			setCandidates(candidatesData);
			const totalCandidates = candidatesData.length;
			if (totalCandidates > 0) {
				setFormData(prev => ({
					...prev,
					dialogs: {
						...prev.dialogs,
						allDialogs: totalCandidates,
						sutki: Math.min(prev.dialogs.sutki, totalCandidates)
					}
				}));
			}
		} catch (error) {
			console.error('Failed to load campaign candidates:', error);
			setCandidates([]);
		} finally {
			setLoadingCandidates(false);
		}
	};

	const handleOpenCommunicationMethod = async (method: CandidateCommunicationMethod) => {
		try {
			const targetUrl = await openCandidateCommunicationMethod(method.url);
			window.open(targetUrl, '_blank', 'noopener,noreferrer');
		} catch (error) {
			console.error('Failed to open candidate communication method:', error);
			toast.error('Не удалось открыть способ связи');
		}
	};

	const openRetryCandidateDialog = (candidate: Candidate) => {
		setRetryCandidateDialog({
			isOpen: true,
			candidate,
			isSubmitting: false
		});
	};

	const closeRetryCandidateDialog = () => {
		setRetryCandidateDialog({
			isOpen: false,
			candidate: null,
			isSubmitting: false
		});
	};

	const openEStaffResume = (resumeData: EStaffResumeData) => {
		setSelectedEStaffResume(resumeData);
		setEstaffResumeModalOpen(true);
	};

	const handleRetryCandidateMessage = async () => {
		const candidate = retryCandidateDialog.candidate;
		const campaignId = candidate?.campaignId ?? selectedCampaign;
		if (!candidate || !campaignId) {
			return;
		}

		setRetryCandidateDialog(prev => ({ ...prev, isSubmitting: true }));
		try {
			await retryCampaignCandidateMessage(campaignId, candidate.id);
			setCandidates(prev => prev.map(item => item.id === candidate.id
				? { ...item, status: 'PENDING', skipReason: null, errorCode: null }
				: item
			));
			toast.success('Сообщение поставлено в очередь повторной отправки');
			closeRetryCandidateDialog();
		} catch (error) {
			console.error('Failed to retry campaign candidate message:', error);
			toast.error('Не удалось поставить сообщение в очередь повторной отправки');
			setRetryCandidateDialog(prev => ({ ...prev, isSubmitting: false }));
		}
	};

	const loadCampaignStatsData = async (campaignId: string) => {
		try {
			const stats = await getCampaignStats(campaignId);
			setCampaignStats(stats);
		} catch (error) {
			console.error('Failed to load campaign stats:', error);
			setCampaignStats({
				activeDialogs: 0,
				totalDialogs: 0,
				sentMessages: 0
			});
		}
	};

	const fetchMessengerAccounts = async (): Promise<{
		telegram: MessengerAccount[];
		whatsapp: MessengerAccount[];
		max: MessengerAccount[];
	}> => {
		try {
			const tenantId = useAuthStore.getState().user?.companyId;
			const response = await api.get('/messengers', {
				headers: tenantId ? { 'x-tenant-id': String(tenantId) } : {}
			});
			const accountsList = response.data.content || response.data.accounts || response.data || [];

			// Для рассылки подходят только активные и авторизованные аккаунты.
			const telegram = accountsList.filter(
				(acc: MessengerAccount) => acc.messengerType === 'TELEGRAM' && isSendReadyMessengerAccount(acc)
			);

			const max = accountsList.filter(
				(acc: MessengerAccount) => acc.messengerType === 'MAX' && isSendReadyMessengerAccount(acc)
			);

			setTelegramAccounts(telegram);
			setMaxAccounts(max);

			let whatsapp: MessengerAccount[] = [];

			// Fetch global WhatsApp settings (Dialog360)
			try {
				const whatsappResponse = await api.get('/messengers/settings/whatsapp', {
					headers: tenantId ? { 'x-tenant-id': String(tenantId) } : {}
				});

				if (whatsappResponse.data.configured) {
					// Create a virtual account for global WhatsApp
					const globalWhatsappAccount: MessengerAccount = {
						id: 'global-whatsapp',
						accountName: whatsappResponse.data.phoneNumber || 'WhatsApp (Dialog360)',
						phoneNumber: whatsappResponse.data.phoneNumber || '',
						messengerType: 'WHATSAPP',
						authStatus: 'AUTHENTICATED',
						isActive: true
					};
					whatsapp = [globalWhatsappAccount];
					setWhatsappAccounts(whatsapp);
				} else {
					setWhatsappAccounts([]);
					setSelectedWhatsappAccount('');
				}
			} catch (whatsappError) {
				console.error('Error fetching WhatsApp settings:', whatsappError);
				setWhatsappAccounts([]);
			}

			// Don't auto-select here - let loadCampaignDetails handle selection
			// based on saved accountSettings or fallback to first account
			return { telegram, whatsapp, max };
		} catch (error) {
			console.error('Error fetching messenger accounts:', error);
			setTelegramAccounts([]);
			setWhatsappAccounts([]);
			setMaxAccounts([]);
			setSelectedTelegramAccount('');
			setSelectedWhatsappAccount('');
			setSelectedMaxAccount('');
			return { telegram: [], whatsapp: [], max: [] };
		}
	};

	const handleAddNewVacancy = () => {
		setSelectedCampaign(null);
		setSelectedCampaignData(null);
		setCampaignStats({ activeDialogs: 0, totalDialogs: 0, sentMessages: 0 });

		// Reset all form data to initial state
		setFormData({
			campaignName: '',
			dialogs: {
				allDialogs: 10,
				sutki: 10
			},
			responseDelay: {
				min: 15,
				max: 25
			},
			initiationAttempts: 2,
			vacancy: {
				position: '',
				salaryEnabled: false,
				salaryTo: '',
				currency: 'RUB',
				salaryType: 'На руки',
				premium: false,
				highlightSalary: false,
				projectDescription: '',
				companyBenefits: ''
			},
			workType: 'Гибрид',
			workingDay: 'Полный день',
			benefits: [
				{
					text: 'Мы предоставим тебе возможность развиваться. Оплатим внешние курсы, дадим доступ к неограниченному количеству обучающих материалов. Ты получишь возможность выступать на конференциях, проводить обучения (при твоем желании, конечно), участвовать в митапах и вебинарах. Хотим развивать экспертность каждого сотрудника.',
					checked: true
				},
				{
					text: 'Мы считаем, что наряду с развитием интеллектуальным, здорово, когда человек уделяет время и физической форме, поэтому предоставляем возможность занятия спортом вместе с компанией. У нас есть собственные спортивные и киберспортивные команды!:) Будем рады видеть тебя в одной из них.',
					checked: true
				},
				{
					text: 'Можно с уверенностью сказать, что наша компания является уникальной благодаря тому, что ставит работу своих сотрудников в приоритет, команда считается основной ценностью для нас. У нас есть Служба заботы, которая поможет тебе решить не только рабочие, но и все личные проблемы.',
					checked: true
				},
				{
					text: 'Индивидуальный соц. пакет. Наша Служба заботы поможет тебе получить бенефиты, которые актуальны именно для тебя.',
					checked: true
				}
			],
			relocationCity: '',
			employmentOptions: '',
			recruiterName: defaultRecruiterName,
			employmentTypes: {
				tkRf: true,
				gph: true,
				ip: true,
				selfEmployed: true
			},
			isAccreditedIT: true,
			hhVacancyId: '',
			avitoVacancyId: ''
		});

		// Reset messenger priority to default
		setAvitoEnabled(false);
		setMessengerPriority([...DEFAULT_MESSENGER_PRIORITY]);

		// Clear search filters
		setSearchFilters(defaultFiltersState);
	};

	const handleCopyVacancy = async (campaignId: string) => {
		try {
			const campaign = await getCampaignById(campaignId);
			if (!campaign) {
				toast.error('Вакансия не найдена');
				return;
			}

			// Parse salary data
			let salaryData: SalaryData | null = null;
			if (campaign.socialPackage) {
				try {
					salaryData =
						typeof campaign.socialPackage === 'string'
							? JSON.parse(campaign.socialPackage)
							: campaign.socialPackage;
				} catch (e) {
					console.error('Failed to parse socialPackage:', e);
				}
			}

			// Parse bonus data
			let hasBonuses = false;
			if (campaign.customPrompts) {
				try {
					const prompts: CustomPrompts =
						typeof campaign.customPrompts === 'string' ? JSON.parse(campaign.customPrompts) : campaign.customPrompts;
					hasBonuses = prompts.hasBonuses || false;
				} catch (e) {
					console.error('Failed to parse customPrompts:', e);
				}
			}

			// Fill form with campaign data
			setFormData((prev) => ({
				...prev,
				campaignName: `${campaign.name} (копия)`,
				vacancy: {
					...prev.vacancy,
					position: campaign.targetPosition || '',
					projectDescription: campaign.requirements || campaign.description || '',
					companyBenefits: campaign.companyDescription || campaign.teamDescription || '',
					salaryEnabled: !!campaign.ndaText,
					salaryTo: salaryData?.max ? String(salaryData.max) : '',
					currency: salaryData?.currency || 'RUB',
					salaryType: salaryData?.isGross ? 'До вычета налогов' : 'На руки',
					premium: hasBonuses,
					highlightSalary: salaryData?.showSalary || false
				},
				dialogs: {
					allDialogs: campaign.maxActiveDialogs || 10,
					sutki: Math.min(campaign.dailyLimit || 10, campaign.maxActiveDialogs || 10)
				},
				workType:
					campaign.workFormat === 'REMOTE'
						? 'Удаленно'
						: campaign.workFormat === 'OFFICE'
						? 'Офис'
						: campaign.workFormat === 'HYBRID'
						? 'Гибрид'
						: prev.workType
			}));

			// Load search filters if they exist
			if ((campaign as any).searchFilters) {
				try {
					const filters =
						typeof (campaign as any).searchFilters === 'string'
							? JSON.parse((campaign as any).searchFilters)
							: (campaign as any).searchFilters;
					setSearchFilters((prev) => ({
						...prev,
						...filters
					}));
				} catch (e) {
					console.error('Failed to parse searchFilters:', e);
				}
			}

			// Clear selected vacancy to show "new vacancy" mode
			setSelectedCampaign(null);
			setSelectedCampaignData(null);
			setCampaignStats(null);

			toast.success('Вакансия скопирована! Внесите изменения и нажмите "Создать"');
		} catch (error) {
			console.error('Failed to copy vacancy:', error);
			toast.error('Ошибка при копировании вакансии');
		}
	};

	const handleStopCampaign = async () => {
		if (!selectedCampaign) return;

		setConfirmDialog({
			isOpen: true,
			title: 'Остановить кампанию',
			message: 'Вы уверены, что хотите остановить эту кампанию?',
			onConfirm: async () => {
				try {
					await stopCampaign(selectedCampaign);
					await loadCampaigns();
					const updatedCampaign = await getCampaignById(selectedCampaign);
					setSelectedCampaignData(updatedCampaign);
					toast.success('Кампания успешно остановлена');
				} catch (error) {
					console.error('Failed to stop campaign:', error);
					toast.error('Не удалось остановить кампанию');
				}
				setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
			}
		});
	};

	const handleArchiveCampaign = async (campaignId: string) => {
		setConfirmDialog({
			isOpen: true,
			title: 'Архивировать вакансию',
			message: 'Вы уверены, что хотите архивировать эту вакансию?',
			onConfirm: async () => {
				try {
					await archiveCampaign(campaignId);
					await loadCampaigns();
					toast.success('Вакансия успешно архивирована');
				} catch (error) {
					console.error('Failed to archive campaign:', error);
					toast.error('Не удалось архивировать вакансию');
				}
				setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
			}
		});
	};

	// Валидация настроек мессенджеров перед сохранением/созданием/запуском
	const validateMessengerSettings = ({ requireEnabledMessenger = true }: { requireEnabledMessenger?: boolean } = {}): boolean => {
		const currentSource = getCampaignSourceKind(selectedCampaignDataRef.current);
		const allowedMessengers = new Set(getAllowedMessengersForSource(currentSource));
		if (selectedTelegramAccount && !telegramAccounts.some((account) => String(account.id) === selectedTelegramAccount)) {
			toast.error('Выбранный Telegram-аккаунт недоступен. Выберите актуальный аккаунт.');
			return false;
		}
		if (selectedMaxAccount && !maxAccounts.some((account) => String(account.id) === selectedMaxAccount)) {
			toast.error('Выбранный Max-аккаунт недоступен. Выберите актуальный аккаунт.');
			return false;
		}

		const enabledMessengers: string[] = [];
		if (avitoEnabled && allowedMessengers.has('AVITO')) enabledMessengers.push('AVITO');
		if (selectedTelegramAccount && allowedMessengers.has('TELEGRAM')) enabledMessengers.push('TELEGRAM');
		if (selectedWhatsappAccount && allowedMessengers.has('WHATSAPP')) enabledMessengers.push('WHATSAPP');
		if (selectedMaxAccount && allowedMessengers.has('MAX')) enabledMessengers.push('MAX');
		if (formData.hhVacancyId && allowedMessengers.has('HH')) enabledMessengers.push('HH');
		const normalizedPriority = normalizeMessengerPriorityForSource(messengerPriority, currentSource);

		// Проверка: должен быть выбран хотя бы один мессенджер
		if (enabledMessengers.length === 0) {
			if (requireEnabledMessenger) {
				toast.error('Необходимо выбрать хотя бы один мессенджер для рассылки');
				return false;
			}
			return true;
		}

		if (enabledMessengers.includes('AVITO') && normalizedPriority.includes('AVITO') && !formData.avitoVacancyId) {
			toast.error(displayedAvitoVacancyId ? AVITO_VACANCY_CONFIRM_REQUIRED_MESSAGE : AVITO_VACANCY_LINK_REQUIRED_MESSAGE);
			return false;
		}

		// Проверка приоритета только среди включённых мессенджеров: HH в сохранённом priority
		// не должен блокировать Telegram-only/E-Staff-only рассылку, если HH не выбран.
		for (const messenger of normalizedPriority) {
			if (!enabledMessengers.includes(messenger)) {
				continue;
			}
			if (messenger === 'HH') {
				if (!formData.hhVacancyId) {
					toast.error('Мессенджер HH указан в приоритете, но вакансия HH не выбрана. Выберите вакансию HH или уберите HH из приоритета');
					return false;
				}
				break;
			}
			break;
		}

		return true;
	};

	const buildAccountSettingsPayload = () => {
		const currentSource = getCampaignSourceKind(selectedCampaignDataRef.current);
		const allowedMessengers = new Set(getAllowedMessengersForSource(currentSource));
		const enabledMessengers: string[] = [
			...(avitoEnabled && allowedMessengers.has('AVITO') ? ['AVITO'] : []),
			...(selectedTelegramAccount && allowedMessengers.has('TELEGRAM') ? ['TELEGRAM'] : []),
			...(selectedWhatsappAccount && allowedMessengers.has('WHATSAPP') ? ['WHATSAPP'] : []),
			...(selectedMaxAccount && allowedMessengers.has('MAX') ? ['MAX'] : []),
			...(formData.hhVacancyId && allowedMessengers.has('HH') ? ['HH'] : [])
		];
		const normalizedPriority = normalizeMessengerPriorityForSource(messengerPriority, currentSource);
		const messengerPriorityForEnabled = [
			...normalizedPriority.filter((messenger) => enabledMessengers.includes(messenger)),
			...enabledMessengers.filter((messenger) => !normalizedPriority.includes(messenger))
		];

		return {
			telegramAccountId: selectedTelegramAccount || null,
			whatsappAccountId: selectedWhatsappAccount || null,
			maxAccountId: selectedMaxAccount || null,
			avitoAccountId: null,
			messengerPriority: messengerPriorityForEnabled,
			enabledMessengers
		};
	};

	const resolveAvitoVacancyIdForPayload = (freshVacancyData: unknown, currentAllowsAvito: boolean): string | null => {
		if (!currentAllowsAvito || !formData.avitoVacancyId) {
			return '';
		}

		const freshLinkedAvitoVacancyId = getLinkedAvitoVacancyId(freshVacancyData);
		if (freshVacancyData && !freshLinkedAvitoVacancyId) {
			return null;
		}

		return freshLinkedAvitoVacancyId || formData.avitoVacancyId;
	};

	const handleDeleteCampaign = async (campaignId: string) => {
		setConfirmDialog({
			isOpen: true,
			title: 'Удалить вакансию',
			message: 'Вы уверены, что хотите удалить эту вакансию? Это действие необратимо!',
			onConfirm: async () => {
				try {
					await deleteCampaign(campaignId);
					await loadCampaigns();
					toast.success('Вакансия успешно удалена');
				} catch (error) {
					console.error('Failed to delete campaign:', error);
					toast.error('Не удалось удалить вакансию');
				}
				setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
			}
		});
	};

	const handleSaveCampaign = async (): Promise<boolean> => {
		// Use ref to get latest value (avoids stale closure issue)
		const currentCampaign = selectedCampaignDataRef.current;
		if (!selectedCampaign || !currentCampaign) {
			setSaveDialog({
				isOpen: true,
				status: 'error',
				message: 'Не выбрана рассылка для сохранения'
			});
			return false;
		}

		if (!validateMessengerSettings({ requireEnabledMessenger: false })) {
			return false;
		}

		setIsLaunching(true);

		// Show loading modal
		setSaveDialog({
			isOpen: true,
			status: 'loading',
			message: 'Сохранение настроек...'
		});

		try {
			const currentSource = getCampaignSourceKind(currentCampaign);
			const currentAllowsHh = currentSource !== 'avito';
			const currentAllowsAvito = currentSource !== 'hh';
			const positionIdForCampaign = currentCampaign.positionId || selectedVacancyId;
			let freshVacancyData = null;
			if (positionIdForCampaign) {
				try {
					freshVacancyData = await apiService.getPosition(positionIdForCampaign);
				} catch (vacancyError) {
					console.error('Error fetching vacancy data:', vacancyError);
					if (currentAllowsAvito && formData.avitoVacancyId) {
						const message = 'Не удалось проверить привязанную вакансию Avito. Попробуйте сохранить ещё раз.';
						setSaveDialog({
							isOpen: true,
							status: 'error',
							message
						});
						toast.error(message);
						return false;
					}
				}
			}
			const avitoVacancyIdForPayload = resolveAvitoVacancyIdForPayload(freshVacancyData, currentAllowsAvito);
			if (avitoVacancyIdForPayload === null) {
				setSaveDialog({
					isOpen: true,
					status: 'error',
					message: AVITO_VACANCY_LINK_REQUIRED_MESSAGE
				});
				toast.error(AVITO_VACANCY_LINK_REQUIRED_MESSAGE);
				return false;
			}

			// Build campaign update payload
			// Exclude read-only fields (id, createdAt, updatedAt, status, totalCandidates) from the update
			const { id, createdAt, updatedAt, status, ...existingData } = currentCampaign as any;
			// Also remove totalCandidates if it exists
			delete (existingData as any).totalCandidates;
			delete (existingData as any).processedCandidates;
			delete (existingData as any).activeDialogs;
			delete (existingData as any).dailyProcessed;
			delete (existingData as any).startedAt;
			delete (existingData as any).completedAt;
			delete (existingData as any).statistics;
			delete (existingData as any).employmentType;
			delete (existingData as any).socialPackage;
			delete (existingData as any).salaryDisclosure;
			delete (existingData as any).ndaText;
			delete (existingData as any).accountSettings;

			// Prepare vacancy-synced fields (from fresh vacancy data if available)
			// Backend CampaignUpdateDto accepts: salaryRange, hasBonuses, etc.
			const vacancySyncedFields = freshVacancyData
				? {
						// Sync vacancy title to targetPosition
						targetPosition: freshVacancyData.title || formData.vacancy.position,
						// Sync vacancy description to requirements
						requirements: freshVacancyData.description || formData.vacancy.projectDescription,
						// Sync company description
						companyDescription: freshVacancyData.companyDescription || formData.vacancy.companyBenefits,
						// Sync relocation fields
						relocationCountry: freshVacancyData.relocationLocation || formData.relocationCity,
						relocationSupport: freshVacancyData.relocationAssistance || formData.employmentOptions,
						// Sync work format
						workFormat: freshVacancyData.workFormat
							? freshVacancyData.workFormat === 'Удаленка' || freshVacancyData.workFormat === 'REMOTE'
								? 'REMOTE'
								: freshVacancyData.workFormat === 'Офис' || freshVacancyData.workFormat === 'OFFICE'
								? 'OFFICE'
								: 'HYBRID'
							: formData.workType === 'Удаленно'
							? 'REMOTE'
							: formData.workType === 'Офис'
							? 'OFFICE'
							: 'HYBRID',
						// Sync salary data (backend expects salaryRange, not socialPackage)
						salaryRange: {
							max: freshVacancyData.salaryTo ?? (formData.vacancy.salaryTo ? parseInt(formData.vacancy.salaryTo) : null),
							currency: formData.vacancy.currency || 'RUB',
							isGross: formData.vacancy.salaryType === 'До вычета налогов',
							showSalary: freshVacancyData.salarySpecified ?? formData.vacancy.highlightSalary
						},
						// Sync bonuses (separate field in DTO)
						hasBonuses: freshVacancyData.hasBonuses ?? formData.vacancy.premium
				  }
				: {
						targetPosition: formData.vacancy.position,
						requirements: formData.vacancy.projectDescription,
						companyDescription: formData.vacancy.companyBenefits,
						relocationCountry: formData.relocationCity,
						relocationSupport: formData.employmentOptions,
						workFormat:
							formData.workType === 'Удаленно' ? 'REMOTE' : formData.workType === 'Офис' ? 'OFFICE' : 'HYBRID',
						salaryRange: {
							max: formData.vacancy.salaryTo ? parseInt(formData.vacancy.salaryTo) : null,
							currency: formData.vacancy.currency || 'RUB',
							isGross: formData.vacancy.salaryType === 'До вычета налогов',
							showSalary: formData.vacancy.highlightSalary
						},
						hasBonuses: formData.vacancy.premium
				  };

			const campaignData: Partial<Campaign> = {
				// Preserve existing fields (except read-only ones)
				...existingData,

				// Update only the fields that are editable in the form
				name: formData.campaignName,
				recruiterName: formData.recruiterName,
				// Apply vacancy-synced fields
				...vacancySyncedFields,
				// Other form fields that are not synced from vacancy
				employmentTkRf: formData.employmentTypes.tkRf,
				employmentGph: formData.employmentTypes.gph,
				employmentIp: formData.employmentTypes.ip,
				employmentSelfEmployed: formData.employmentTypes.selfEmployed,
				isAccreditedIT: formData.isAccreditedIT,
				hhVacancyId: currentAllowsHh ? (formData.hhVacancyId || undefined) : (null as any),
				avitoVacancyId: currentAllowsAvito ? avitoVacancyIdForPayload : '',
				firstMessageTemplate: firstMessageTemplate || undefined,
				firstMessageTemplateClarify: firstMessageTemplateClarify || undefined,
				approvalMessageTemplate: approvalTemplate,
				rejectionMessageTemplate: rejectionTemplate,
				responseDelayMin: formData.responseDelay.min,
				responseDelayMax: formData.responseDelay.max,
				attempts: formData.initiationAttempts,
				maxActiveDialogs: formData.dialogs.allDialogs,
				dailyLimit: formData.dialogs.sutki,
				contactWindowStart: contactWindow.start,
				contactWindowEnd: contactWindow.end,
				//ndaText: formData.vacancy.salaryEnabled ? 'NDA' : undefined,
				accountSettings: buildAccountSettingsPayload(),
				// Explicitly preserve aiScreeningEnabled and hrInterviewEnabled to prevent accidental deletion
				aiScreeningEnabled: currentCampaign.aiScreeningEnabled,
				hrInterviewEnabled: currentCampaign.hrInterviewEnabled
			};

			await updateCampaign(selectedCampaign, campaignData);

			// Show success modal
			setSaveDialog({
				isOpen: true,
				status: 'success',
				message: 'Настройки успешно сохранены!'
			});

			await loadCampaigns();
			const updatedCampaign = await getCampaignById(selectedCampaign);
			setSelectedCampaignData(updatedCampaign);
			setFirstMessageTemplateOriginal(firstMessageTemplate);
			setFirstMessageTemplateClarifyOriginal(firstMessageTemplateClarify);
			setApprovalTemplateOriginal(approvalTemplate);
			setRejectionTemplateOriginal(rejectionTemplate);

			// Auto-close success modal after 2 seconds
			setTimeout(() => {
				setSaveDialog({ isOpen: false, status: 'loading', message: '' });
			}, 2000);
			return true;
		} catch (error: any) {
			console.error('Error saving campaign:', error);

			const backendMessage = error?.response?.data?.message;
			// Show error modal
			setSaveDialog({
				isOpen: true,
				status: 'error',
				message: backendMessage || 'Ошибка при сохранении настроек'
			});
			return false;
		} finally {
			setIsLaunching(false);
		}
	};

	const handleLaunchCampaign = async () => {
		if (!selectedCampaign) {
			toast.error('Пожалуйста, выберите кампанию для запуска');
			return;
		}

		if (!validateMessengerSettings()) {
			return;
		}

		setIsLaunching(true);

		try {
			await startCampaign(selectedCampaign);
			toast.success('Кампания успешно запущена!');
			await loadCampaigns();
			const updatedCampaign = await getCampaignById(selectedCampaign);
			setSelectedCampaignData(updatedCampaign);
		} catch (error: any) {
			console.error('Error launching campaign:', error);
			const backendMessage = error?.response?.data?.message;
			toast.error(backendMessage || 'Ошибка при запуске кампании');
		} finally {
			setIsLaunching(false);
		}
	};

	const handleCreateCampaign = async () => {
		setIsLaunching(true);

		try {
			// Validate required fields
			const errors: Record<string, boolean> = {};
			// campaignName is auto-populated from campaign data, no validation needed
			// Other fields (position, salary, etc.) are pulled from vacancy context
			if (!formData.recruiterName) errors.recruiterName = true;

			if (Object.keys(errors).length > 0) {
				setValidationErrors(errors);
				toast.error('Пожалуйста, заполните все обязательные поля');
				setIsLaunching(false);
				return;
			}

			if (!validateMessengerSettings()) {
				setIsLaunching(false);
				return;
			}

			// Build campaign creation payload
			const tenantUserId = useAuthStore.getState().user?.id;
			const currentSource = getCampaignSourceKind(selectedCampaignDataRef.current);
			const currentAllowsHh = currentSource !== 'avito';
			const currentAllowsAvito = currentSource !== 'hh';
			let freshVacancyData = null;
			if (selectedVacancyId) {
				try {
					freshVacancyData = await apiService.getPosition(selectedVacancyId);
				} catch (vacancyError) {
					console.error('Error fetching vacancy data:', vacancyError);
					if (currentAllowsAvito && formData.avitoVacancyId) {
						toast.error('Не удалось проверить привязанную вакансию Avito. Попробуйте создать рассылку ещё раз.');
						setIsLaunching(false);
						return;
					}
				}
			}
			const avitoVacancyIdForPayload = resolveAvitoVacancyIdForPayload(freshVacancyData, currentAllowsAvito);
			if (avitoVacancyIdForPayload === null) {
				toast.error(AVITO_VACANCY_LINK_REQUIRED_MESSAGE);
				setIsLaunching(false);
				return;
			}
			const campaignData: Partial<Campaign> = {
				name: formData.campaignName,
				recruiterName: formData.recruiterName,
				recruiterId: tenantUserId ? String(tenantUserId) : undefined,
				positionId: selectedVacancyId || undefined,
				targetPosition: formData.vacancy.position,
				requirements: formData.vacancy.projectDescription,
				companyDescription: formData.vacancy.companyBenefits,
				relocationCountry: formData.relocationCity,
				relocationSupport: formData.employmentOptions,
				employmentTkRf: formData.employmentTypes.tkRf,
				employmentGph: formData.employmentTypes.gph,
				employmentIp: formData.employmentTypes.ip,
				employmentSelfEmployed: formData.employmentTypes.selfEmployed,
				isAccreditedIT: formData.isAccreditedIT,
				hhVacancyId: currentAllowsHh ? (formData.hhVacancyId || undefined) : undefined,
				avitoVacancyId: currentAllowsAvito ? (avitoVacancyIdForPayload || undefined) : undefined,
				responseDelayMin: formData.responseDelay.min,
				responseDelayMax: formData.responseDelay.max,
				attempts: formData.initiationAttempts,
				maxActiveDialogs: formData.dialogs.allDialogs,
				dailyLimit: formData.dialogs.sutki,
				contactWindowStart: contactWindow.start,
				contactWindowEnd: contactWindow.end,
				workFormat:
					formData.workType === 'Удаленно' ? 'REMOTE' : formData.workType === 'Офис' ? 'OFFICE' : 'HYBRID',
				socialPackage: {
					max: formData.vacancy.salaryTo ? parseInt(formData.vacancy.salaryTo) : null,
					currency: formData.vacancy.currency,
					isGross: formData.vacancy.salaryType === 'До вычета налогов',
					showSalary: formData.vacancy.highlightSalary
				} as SalaryData,
				customPrompts: {
					hasBonuses: formData.vacancy.premium
				} as CustomPrompts,
				ndaText: formData.vacancy.salaryEnabled ? 'NDA' : undefined,
				accountSettings: buildAccountSettingsPayload(),
				approvalMessageTemplate: approvalTemplate,
				rejectionMessageTemplate: rejectionTemplate,
				status: 'DRAFT',
				aiScreeningEnabled: (selectedVacancy as any)?.aiScreeningEnabled === false ? false : true,
				hrInterviewEnabled: (selectedVacancy as any)?.hrInterviewEnabled === false ? false : true
			};

			// TASKNEIROKLYUCH-744: silent resolve E-Staff vacancy_id из source-link.
			// Без явной передачи estaffVacancyId backend пропустит E-Staff sync
			// (CampaignWorker.processCandidate:312). Backend (ai-recruiter auto-resolve в
			// MR #112) подстрахует, но фронт делает быстрый путь без round-trip.
			// Followup для merged #200: убрана модалка ensureEstaffLink — она была
			// слишком инвазивной для пользователей, которые E-Staff не используют.
			// Настройка source-link — отдельный flow в UI вакансии.
			if (selectedVacancyId) {
				try {
					const estaffVacancyId = await apiService.resolveSourceVacancyId(
						Number(selectedVacancyId), 'ESTAFF'
					);
					if (estaffVacancyId) {
						(campaignData as any).estaffVacancyId = estaffVacancyId;
					}
				} catch (err) {
					// Silent: backend подтянет если сможет
					console.warn('Failed to resolve E-Staff source link', err);
				}
			}

			const created = await createCampaign(campaignData);
			toast.success('Кампания успешно создана!');
			await loadCampaigns();
			setSelectedCampaign(String(created.id));
		} catch (error: any) {
			console.error('Error creating campaign:', error);
			const backendMessage = error?.response?.data?.message;
			toast.error(backendMessage || 'Ошибка при создании кампании');
		} finally {
			setIsLaunching(false);
		}
	};

	const handleChange = (field: keyof FormDataState, value: any) => {
		setFormData((prev) => ({
			...prev,
			[field]: value
		}));
	};

	const handleNestedChange = (parent: keyof FormDataState, field: string, value: any) => {
		setFormData((prev) => ({
			...prev,
			[parent]: {
				...(prev[parent] as any),
				[field]: value
			}
		}));
	};

	const toggleBenefit = (index: number) => {
		const newBenefits = [...formData.benefits];
		newBenefits[index].checked = !newBenefits[index].checked;
		handleChange('benefits', newBenefits);
	};

	const handleSearchFilterChange = (updates: Partial<FiltersState>) => {
		setSearchFilters((prev) => ({
			...prev,
			...updates
		}));
	};

	const handleClearSearchFilters = () => {
		setSearchFilters(defaultFiltersState);
	};

	const closeConfirmDialog = () => {
		setConfirmDialog({
			isOpen: false,
			title: '',
			message: '',
			onConfirm: () => {}
		});
	};

	const getInputClassName = (
		fieldName: string,
		baseClassName = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent'
	) => {
		return `${baseClassName} ${
			validationErrors[fieldName]
				? 'border-red-500 focus:ring-red-500 bg-red-50'
				: 'border-gray-300 focus:ring-indigo-500'
		}`;
	};

	const clearValidationError = (fieldName: string) => {
		if (validationErrors[fieldName]) {
			setValidationErrors((prev) => ({ ...prev, [fieldName]: false }));
		}
	};

	const activeFirstMessageTemplate =
		firstMessageTemplateTab === 'cold' ? firstMessageTemplate : firstMessageTemplateClarify;
	const activeAccountFirstMessageTemplate =
		firstMessageTemplateTab === 'cold' ? accountTemplate : accountClarifyTemplate;
	const activePositionFirstMessageTemplate =
		firstMessageTemplateTab === 'cold' ? positionTemplate : positionClarifyTemplate;
	const setActiveFirstMessageTemplate = (value: string | ((prev: string) => string)) => {
		if (firstMessageTemplateTab === 'cold') {
			setFirstMessageTemplate(value);
		} else {
			setFirstMessageTemplateClarify(value);
		}
	};

	return (
		<div className="h-full overflow-y-auto bg-[#f5f6f1]">
			<div className={`flex gap-6 ${embedded ? 'p-0' : 'p-6'} pt-0 max-w-full relative`}>
				{/* Left Sidebar - Search Filters
				<div className="w-96 flex-shrink-0 overflow-y-auto overflow-x-visible">
					<SearchFilters2
						filters={searchFilters}
						onFiltersUpdate={handleSearchFilterChange}
						onClearFilters={handleClearSearchFilters}
					/>
				</div>*/}

				{/* Main Content Area */}
				<div className="flex-1 min-w-0 space-y-6">
					{/* Show message if no archived vacancies */}
					{showArchived && vacancies.length === 0 ? (
						<div className="bg-white rounded-lg p-12 shadow-sm text-center">
							<p className="text-gray-500 text-lg">Нет архивных вакансий</p>
						</div>
					) : (
						<>
							{/* Dialog limits and Contact window */}
							<div className="bg-white rounded-lg p-6 shadow-sm space-y-4">
							<h3 className="text-lg font-semibold text-gray-900">Настройки диалога</h3>
								<div className="flex items-center gap-6 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
									{/* Daily dialogs */}
                                    <div className="flex items-center gap-3">
                                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: colors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<label className="text-sm font-medium text-gray-700 whitespace-nowrap">Диалогов в сутки:</label>
										<input
											type="number"
											value={formData.dialogs.sutki}
											onChange={(e) => {
												const maxDialogs = candidates.length > 0 ? candidates.length : formData.dialogs.allDialogs;
												const value = Math.min(Math.max(1, parseInt(e.target.value) || 1), maxDialogs);
												handleNestedChange('dialogs', 'sutki', value);
											}}
											min="1"
											max={candidates.length > 0 ? candidates.length : formData.dialogs.allDialogs}
											className="w-20 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
										/>
										{candidates.length > 0 && (
											<span className="text-xs text-gray-500">(макс. {candidates.length})</span>
										)}
									</div>

									<div className="h-6 w-px bg-gray-300"></div>

									{/* Contact time */}
									<div className="flex items-center gap-3">
										<svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<label className="text-sm font-medium text-gray-700 whitespace-nowrap">Время контакта:</label>
										<div className="flex items-center gap-2">
											<input
												type="number"
												value={contactWindow.start}
												onChange={(e) => setContactWindow({ ...contactWindow, start: parseInt(e.target.value) || 0 })}
												min="0"
												max="23"
												className="w-14 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center"
											/>
											<span className="text-gray-500 text-sm">—</span>
											<input
												type="number"
												value={contactWindow.end}
												onChange={(e) => setContactWindow({ ...contactWindow, end: parseInt(e.target.value) || 23 })}
												min="0"
												max="23"
												className="w-14 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center"
											/>
										</div>
									</div>
								</div>

								{/* Second row - Response delay and Initiation attempts */}
                                <div className="flex items-center gap-6 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                                    {/* Response delay */}
                                    <div className="flex items-center gap-3">
                                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: colors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<label className="text-sm font-medium text-gray-700 whitespace-nowrap">Задержка ответа (сек):</label>
										<div className="flex items-center gap-2">
											<input
												type="number"
												value={formData.responseDelay?.min || 15}
												min="1"
												max="60"
												onChange={(e) => {
													const value = Math.min(60, Math.max(1, parseInt(e.target.value) || 15));
													setFormData((prev) => ({
														...prev,
														responseDelay: {
															...(prev.responseDelay || {}),
															min: value
														}
													}));
												}}
												className="w-14 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
											/>
											<span className="text-gray-500 text-sm">—</span>
											<input
												type="number"
												value={formData.responseDelay?.max || 25}
												min="1"
												max="60"
												onChange={(e) => {
													const value = Math.min(60, Math.max(1, parseInt(e.target.value) || 25));
													setFormData((prev) => ({
														...prev,
														responseDelay: {
															...(prev.responseDelay || {}),
															max: value
														}
													}));
												}}
												className="w-14 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center"
											/>
										</div>
									</div>

									<div className="h-6 w-px bg-gray-300"></div>

									{/* Initiation attempts */}
									<div className="flex items-center gap-3">
										<svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
											/>
										</svg>
										<label className="text-sm font-medium text-gray-700 whitespace-nowrap">Количество попыток инициации:</label>
										<input
											type="number"
											value={formData.initiationAttempts || 2}
											onChange={(e) =>
												setFormData((prev) => ({
													...prev,
													initiationAttempts: parseInt(e.target.value) || 2
												}))
											}
											min="1"
											max="10"
											className="w-20 px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-center"
										/>
									</div>
								</div>

								{/* Messenger account selectors */}
								<div className="space-y-2 pt-2">
									<h4 className="text-sm pb-2 font-medium text-gray-900">
										AI-рекрутер аккаунты <span className="text-red-500">*</span>
									</h4>

									<div
										className={`grid grid-cols-2 gap-4 pb-4 p-3 rounded-lg ${
											validationErrors.messenger ? 'border-2 border-red-500 bg-red-50' : ''
										}`}
									>
										{/* AI Recruiter Name */}
										<div className="space-y-1">
											<label className="block text-sm text-gray-700">
												Имя AI-рекрутера <span className="text-red-500">*</span>
											</label>
											<input
												type="text"
												value={formData.recruiterName}
												onChange={(e) => {
													handleChange('recruiterName', e.target.value);
													if (validationErrors.recruiterName) {
														setValidationErrors((prev) => ({ ...prev, recruiterName: false }));
													}
												}}
												placeholder="Введите имя рекрутера"
												className={`w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
													validationErrors.recruiterName
														? 'border-red-500 focus:ring-red-500'
														: 'border-gray-300 focus:ring-indigo-500'
												}`}
											/>
										</div>

										{/* Telegram */}
										<div className="space-y-1">
											<label className="block text-sm text-gray-700">Telegram</label>
											<DropdownMenu.Root>
												<DropdownMenu.Trigger asChild>
													<button
														ref={(el) => {
															if (el && !telegramTriggerRef.current) {
																telegramTriggerRef.current = el;
																setTelegramTriggerWidth(el.offsetWidth);
															}
														}}
									className="w-full flex items-center justify-between px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none text-sm"
									onFocus={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${colors.primary}`; }}
									onBlur={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = ''; }}
													>
														<span className={selectedTelegramAccount ? 'text-gray-700' : 'text-gray-500'}>
															{selectedTelegramAccount
																? telegramAccounts.find((a) => a.id === selectedTelegramAccount)?.accountName ||
																  telegramAccounts.find((a) => a.id === selectedTelegramAccount)?.phoneNumber ||
																  'Выберите Telegram-аккаунт'
																: 'Не выбран'}
														</span>
														<ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
													</button>
												</DropdownMenu.Trigger>
												<DropdownMenu.Portal>
													<DropdownMenu.Content
														className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto z-50"
														align="start"
														sideOffset={4}
														style={{ width: telegramTriggerWidth }}
													>
										<DropdownMenu.Item
											className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
															onSelect={() => setSelectedTelegramAccount('')}
											style={selectedTelegramAccount === '' ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
														>
															Не выбран
														</DropdownMenu.Item>
										{telegramAccounts.map((account) => (
											<DropdownMenu.Item
												key={account.id}
												className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
												onSelect={() => {
													setSelectedTelegramAccount(account.id);
													if (validationErrors.messenger) {
														setValidationErrors((prev) => ({ ...prev, messenger: false }));
													}
												}}
												style={selectedTelegramAccount === account.id ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
											>
												{account.accountName || account.phoneNumber || 'Telegram'}
											</DropdownMenu.Item>
										))}
													</DropdownMenu.Content>
												</DropdownMenu.Portal>
											</DropdownMenu.Root>
										</div>

										{/* WhatsApp */}
										<div className="space-y-1">
											<label className="block text-sm text-gray-700">WhatsApp</label>
											<DropdownMenu.Root>
												<DropdownMenu.Trigger asChild>
													<button
														ref={(el) => {
															if (el && !whatsappTriggerRef.current) {
																whatsappTriggerRef.current = el;
																setWhatsappTriggerWidth(el.offsetWidth);
															}
														}}
									className="w-full flex items-center justify-between px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none text-sm"
									onFocus={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${colors.primary}`; }}
									onBlur={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = ''; }}
													>
														<span className={selectedWhatsappAccount ? 'text-gray-700' : 'text-gray-500'}>
															{selectedWhatsappAccount
																? whatsappAccounts.find((a) => a.id === selectedWhatsappAccount)?.accountName ||
																  whatsappAccounts.find((a) => a.id === selectedWhatsappAccount)?.phoneNumber ||
																  'WhatsApp'
																: 'Не выбран'}
														</span>
														<ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
													</button>
												</DropdownMenu.Trigger>
												<DropdownMenu.Portal>
													<DropdownMenu.Content
														className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto z-50"
														align="start"
														sideOffset={4}
														style={{ width: whatsappTriggerWidth }}
													>
										<DropdownMenu.Item
											className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
															onSelect={() => setSelectedWhatsappAccount('')}
											style={selectedWhatsappAccount === '' ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
														>
															Не выбран
														</DropdownMenu.Item>
										{whatsappAccounts.map((account) => (
											<DropdownMenu.Item
												key={account.id}
												className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
												onSelect={() => setSelectedWhatsappAccount(account.id)}
												style={selectedWhatsappAccount === account.id ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
											>
												{account.accountName || account.phoneNumber || 'WhatsApp'}
											</DropdownMenu.Item>
										))}
													</DropdownMenu.Content>
												</DropdownMenu.Portal>
											</DropdownMenu.Root>
										</div>

										{/* Max */}
										<div className="space-y-1">
											<label className="block text-sm text-gray-700">Max</label>
											<DropdownMenu.Root>
												<DropdownMenu.Trigger asChild>
													<button
														ref={(el) => {
															if (el && !maxTriggerRef.current) {
																maxTriggerRef.current = el;
																setMaxTriggerWidth(el.offsetWidth);
															}
														}}
									className="w-full flex items-center justify-between px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none text-sm"
									onFocus={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 2px ${colors.primary}`; }}
									onBlur={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = ''; }}
													>
														<span className={selectedMaxAccount ? 'text-gray-700' : 'text-gray-500'}>
															{selectedMaxAccount
																? maxAccounts.find((a) => a.id === selectedMaxAccount)?.accountName ||
																  maxAccounts.find((a) => a.id === selectedMaxAccount)?.phoneNumber ||
																  'Max'
																: 'Не выбран'}
														</span>
														<ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
													</button>
												</DropdownMenu.Trigger>
												<DropdownMenu.Portal>
													<DropdownMenu.Content
														className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto z-50"
														align="start"
														sideOffset={4}
														style={{ width: maxTriggerWidth }}
													>
										<DropdownMenu.Item
											className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
															onSelect={() => setSelectedMaxAccount('')}
											style={selectedMaxAccount === '' ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
														>
															Не выбран
														</DropdownMenu.Item>
										{maxAccounts.map((account) => (
											<DropdownMenu.Item
												key={account.id}
												className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer focus:outline-none"
												onSelect={() => setSelectedMaxAccount(account.id)}
												style={selectedMaxAccount === account.id ? { backgroundColor: hexToRgba(colors.primary, 0.1), color: colors.primary } : undefined}
											>
												{account.accountName || account.phoneNumber || 'Max'}
											</DropdownMenu.Item>
										))}
													</DropdownMenu.Content>
												</DropdownMenu.Portal>
											</DropdownMenu.Root>
										</div>

									{selectedAllowsAvito && (
										<div className="col-span-2 mt-2 rounded-lg border border-gray-200 bg-white px-3 py-3">
											<label className="flex items-start gap-3">
												<input
													type="checkbox"
													checked={Boolean(formData.avitoVacancyId)}
													onChange={(event) => {
														if (!event.target.checked) {
															handleChange('avitoVacancyId', '');
															return;
														}
														const nextAvitoVacancyId = linkedAvitoVacancyId || displayedAvitoVacancyId;
														if (!nextAvitoVacancyId) {
															toast.error(AVITO_VACANCY_LINK_REQUIRED_MESSAGE);
															return;
														}
														handleChange('avitoVacancyId', nextAvitoVacancyId);
													}}
													className="mt-1 h-4 w-4 rounded border-gray-300"
													style={{ accentColor: colors.primary }}
												/>
												<span className="min-w-0 flex-1">
													<span className="block text-sm font-medium text-gray-700">
														Использовать привязанную вакансию Avito
														<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-600 text-white ml-1.5">Авито</span>
													</span>
													<span className={`mt-1 block text-xs ${displayedAvitoVacancyId ? 'text-gray-500' : 'text-red-600'}`}>
														{displayedAvitoVacancyId
															? `Будет использована ${displayedAvitoVacancyTitle}, Avito ID ${displayedAvitoVacancyId}`
															: AVITO_VACANCY_LINK_REQUIRED_MESSAGE}
													</span>
												</span>
											</label>
										</div>
									)}

									{selectedAllowsHh && (
										<div className="col-span-2 mt-2">
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Вакансия HH.ru
												<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white ml-1.5">hh.ru</span>
											</label>
											<div ref={hhVacancyDropdownRef} className="relative">
												<button
													type="button"
													onClick={() => !hhVacanciesLoading && setHhVacancyDropdownOpen(!hhVacancyDropdownOpen)}
													disabled={hhVacanciesLoading}
													className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent bg-white text-left flex items-center justify-between disabled:bg-gray-100 transition-colors"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												>
													<span className={!formData.hhVacancyId ? 'text-gray-400' : 'text-gray-900'}>
														{hhVacanciesLoading
															? 'Загрузка...'
															: formData.hhVacancyId
																? (() => {
																	const v = hhVacancies.find(v => v.id === formData.hhVacancyId);
																	return v ? `${v.name}${v.area?.name ? ` (${v.area.name})` : ''}` : formData.hhVacancyId;
																})()
																: 'Выберите вакансию...'}
													</span>
													<ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${hhVacancyDropdownOpen ? 'rotate-180' : ''}`} />
												</button>

												{hhVacancyDropdownOpen && (
													<div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
														<button
															type="button"
															onClick={() => { handleChange('hhVacancyId', ''); setHhVacancyDropdownOpen(false); }}
															className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 ${
																!formData.hhVacancyId ? 'bg-gray-50' : ''
															}`}
														>
															<span className="text-gray-400">Не выбрана</span>
														</button>
														{hhVacancies.map((vacancy) => {
															const isSelected = formData.hhVacancyId === vacancy.id;
															return (
																<button
																	key={vacancy.id}
																	type="button"
																	onClick={() => { handleChange('hhVacancyId', vacancy.id); setHhVacancyDropdownOpen(false); }}
																	className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
																		isSelected ? 'bg-gray-50' : ''
																	}`}
																>
																	<span className="text-gray-900">
																		{vacancy.name}{vacancy.area?.name ? ` (${vacancy.area.name})` : ''}
																	</span>
																</button>
															);
														})}
														{hhVacancies.length === 0 && !hhVacanciesLoading && (
															<div className="px-3 py-2 text-sm text-gray-400">Нет доступных вакансий</div>
														)}
													</div>
												)}
											</div>
										</div>
									)}
									</div>

									{/* Messenger Priority Configuration - Collapsible */}
									<div className="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
										<button
											type="button"
											onClick={() => setIsMessengerPriorityExpanded(!isMessengerPriorityExpanded)}
											className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
										>
											<div className="flex items-center gap-2">
												<svg className="w-5 h-5" style={{ color: colors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
													/>
												</svg>
												<h5 className="text-sm font-semibold text-gray-900">Приоритет мессенджеров</h5>
												<span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
													{visibleMessengerPriority.length} настроено
												</span>
											</div>
											<ChevronDownIcon
												className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
													isMessengerPriorityExpanded ? 'rotate-180' : ''
												}`}
											/>
										</button>

										<div
											className={`transition-all duration-300 ease-in-out ${
												isMessengerPriorityExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
											} overflow-hidden`}
										>
											<div className="p-4 border-t border-gray-200" style={{ background: `linear-gradient(135deg, #f9fafb 0%, ${hexToRgba(colors.primary, 0.06)} 100%)` }}>
												<p className="text-xs text-gray-600 mb-3 italic">Порядок попыток доставки сообщений кандидатам</p>
												<div className="space-y-2">
													{visibleMessengerPriority.map((messenger, index) => (
													<div
														key={messenger}
														className="flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-200 transition-colors"
														onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = colors.primary; }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#e5e7eb'; }}
													>
													<span className="text-sm font-bold w-8 h-8 flex items-center justify-center rounded-full" style={{ color: colors.primary, backgroundColor: hexToRgba(colors.primary, 0.15) }}>
																{index + 1}
															</span>
															<div className="flex-1 px-3 py-2 bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
																{messenger === 'TELEGRAM' && (
																	<svg className="w-5 h-5 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor">
																		<path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
																	</svg>
																)}
																{messenger === 'WHATSAPP' && (
																	<svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
																		<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
																	</svg>
																)}
																{messenger === 'MAX' && (
																	<img src="/max_logo_2025.png" alt="Max" className="w-5 h-5 rounded-sm" />
																)}
																{messenger === 'AVITO' && (
																	avitoIconSrc
																		? <img src={avitoIconSrc} alt="Авито" className="w-5 h-5 rounded-sm" />
																		: <span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-green-600 text-[10px] font-bold text-white">A</span>
																)}
																{messenger === 'HH' && (
																	<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
																		<circle cx="12" cy="12" r="12" fill="#D6001C"/>
																		<path d="M6.1 7.8v8h1.4v-3.4h2.3v3.4h1.4v-8h-1.4v3.4H7.5V7.8H6.1zm6 0v8h1.4v-3.4h2.3v3.4H17.2v-8h-1.4v3.4h-2.3V7.8h-1.4z" fill="white"/>
																	</svg>
																)}
																<span className="text-sm font-medium text-gray-800">
																	{messenger === 'TELEGRAM' ? 'Telegram' : messenger === 'WHATSAPP' ? 'WhatsApp' : messenger === 'HH' ? 'HeadHunter' : messenger === 'AVITO' ? 'Авито' : 'Max'}
																</span>
															</div>
															<div className="flex gap-1">
																{index > 0 && (
																	<button
																		type="button"
																		onClick={() => {
																			const newPriority = [...visibleMessengerPriority];
																			[newPriority[index], newPriority[index - 1]] = [newPriority[index - 1], newPriority[index]];
																			setMessengerPriority(newPriority);
																		}}
														className="p-1.5 text-gray-400 rounded transition-colors"
														onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.primary; (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.08); }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
																		title="Переместить вверх"
																	>
																		<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
																		</svg>
																	</button>
																)}
																{index < visibleMessengerPriority.length - 1 && (
																	<button
																		type="button"
																		onClick={() => {
																			const newPriority = [...visibleMessengerPriority];
																			[newPriority[index], newPriority[index + 1]] = [newPriority[index + 1], newPriority[index]];
																			setMessengerPriority(newPriority);
																		}}
														className="p-1.5 text-gray-400 rounded transition-colors"
														onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.primary; (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.08); }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af'; (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
																		title="Переместить вниз"
																	>
																		<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
																		</svg>
																	</button>
																)}
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* First Message Template */}
								<div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
									<div className="px-4 py-3">
										<div className="flex flex-wrap items-center justify-between gap-3 mb-3">
											<div className="flex items-center gap-2">
												<svg className="w-5 h-5" style={{ color: colors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
												</svg>
												<h5 className="text-sm font-semibold text-gray-900">Шаблон первого сообщения</h5>
											</div>
											<div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
												{([
													['cold', 'Холодное сообщение'],
													['clarify', 'Уточнение опыта из отклика'],
												] as const).map(([tab, label]) => {
													const active = firstMessageTemplateTab === tab;
													return (
														<button
															key={tab}
															type="button"
															onClick={() => setFirstMessageTemplateTab(tab)}
															className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
															style={{
																backgroundColor: active ? '#ffffff' : 'transparent',
																color: active ? colors.primary : '#6b7280',
																boxShadow: active ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none'
															}}
														>
															{label}
														</button>
													);
												})}
											</div>
										</div>
										<textarea
											ref={templateTextareaRef}
											value={activeFirstMessageTemplate}
											onChange={(e) => setActiveFirstMessageTemplate(e.target.value)}
											rows={firstMessageTemplateTab === 'clarify' ? 8 : 4}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-vertical"
											style={{ focusRingColor: colors.primary } as any}
											onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.boxShadow = `0 0 0 2px ${hexToRgba(colors.primary, 0.2)}`; }}
											onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
											placeholder="Введите шаблон первого сообщения..."
										/>
										<div className="flex flex-wrap gap-1.5 mt-2">
											{['{{candidate_name}}', '{{company_name}}', '{{recruiter_name}}', '{{position}}'].map((variable) => (
												<button
													key={variable}
													type="button"
													onClick={() => {
														const textarea = templateTextareaRef.current;
														if (!textarea) return;
														const start = textarea.selectionStart;
														const end = textarea.selectionEnd;
														setActiveFirstMessageTemplate(
															activeFirstMessageTemplate.slice(0, start) + variable + activeFirstMessageTemplate.slice(end)
														);
														setTimeout(() => {
															textarea.focus();
															const cursor = start + variable.length;
															textarea.setSelectionRange(cursor, cursor);
														}, 0);
													}}
													className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
												>
													+ {variable}
												</button>
											))}
										</div>
										{/* Кнопки вставки шаблонов */}
										{(activeAccountFirstMessageTemplate || activePositionFirstMessageTemplate) && (
											<div className="flex flex-wrap items-center gap-1.5 mt-2">
												<span className="text-xs text-gray-500 mr-1">Вставить:</span>
												{activeAccountFirstMessageTemplate && (
													<button
														type="button"
														onClick={() => setActiveFirstMessageTemplate(activeAccountFirstMessageTemplate)}
														className="px-2.5 py-1 text-xs rounded border transition-colors"
														style={{
															borderColor: colors.primary,
															color: colors.primary,
															backgroundColor: activeFirstMessageTemplate === activeAccountFirstMessageTemplate ? hexToRgba(colors.primary, 0.1) : 'transparent'
														}}
														onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = hexToRgba(colors.primary, 0.1); }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = activeFirstMessageTemplate === activeAccountFirstMessageTemplate ? hexToRgba(colors.primary, 0.1) : 'transparent'; }}
													>
														Шаблон аккаунта
													</button>
												)}
												{activePositionFirstMessageTemplate && (
													<button
														type="button"
														onClick={() => setActiveFirstMessageTemplate(activePositionFirstMessageTemplate)}
														className="px-2.5 py-1 text-xs rounded border transition-colors"
														style={{
															borderColor: '#10b981',
															color: '#059669',
															backgroundColor: activeFirstMessageTemplate === activePositionFirstMessageTemplate ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
														}}
														onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(16, 185, 129, 0.1)'; }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = activeFirstMessageTemplate === activePositionFirstMessageTemplate ? 'rgba(16, 185, 129, 0.1)' : 'transparent'; }}
													>
														Шаблон вакансии
													</button>
												)}
											</div>
										)}

										{/* Кнопки сохранения */}
										{(firstMessageTemplate !== firstMessageTemplateOriginal || firstMessageTemplateClarify !== firstMessageTemplateClarifyOriginal) && (
											<div className="flex flex-wrap justify-end gap-2 mt-2">
												{(selectedCampaignData?.positionId || selectedVacancyId) && (
													<button
														onClick={async () => {
															const posId = selectedCampaignData?.positionId || selectedVacancyId;
															if (!posId) return;
															setSavingTemplate(true);
															try {
																await savePositionTemplate(posId, {
																	templateText: firstMessageTemplate,
																	clarifyTemplateText: firstMessageTemplateClarify
																});
																if (selectedCampaign) {
																	await updateFirstMessageTemplate(
																		selectedCampaign,
																		firstMessageTemplate,
																		firstMessageTemplateClarify,
																		undefined
																	);
																}
																setFirstMessageTemplateOriginal(firstMessageTemplate);
																setFirstMessageTemplateClarifyOriginal(firstMessageTemplateClarify);
																setPositionTemplate(firstMessageTemplate);
																setPositionClarifyTemplate(firstMessageTemplateClarify);
																toast.success('Шаблон вакансии сохранён');
															} catch (error) {
																console.error('Failed to save position template:', error);
																toast.error('Ошибка сохранения шаблона вакансии');
															} finally {
																setSavingTemplate(false);
															}
														}}
														disabled={savingTemplate}
														className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
														style={{ backgroundColor: '#059669' }}
														onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'; }}
														onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
													>
														{savingTemplate ? 'Сохранение...' : 'Сохранить для вакансии'}
													</button>
												)}
												<button
													onClick={async () => {
														setSavingTemplate(true);
														try {
															await saveAccountTemplate({
																templateText: firstMessageTemplate,
																clarifyTemplateText: firstMessageTemplateClarify
															});
															if (selectedCampaign) {
																await updateFirstMessageTemplate(
																	selectedCampaign,
																	firstMessageTemplate,
																	firstMessageTemplateClarify,
																	undefined
																);
															}
															setFirstMessageTemplateOriginal(firstMessageTemplate);
															setFirstMessageTemplateClarifyOriginal(firstMessageTemplateClarify);
															setAccountTemplate(firstMessageTemplate);
															setAccountClarifyTemplate(firstMessageTemplateClarify);
															toast.success('Шаблон аккаунта сохранён');
														} catch (error) {
															console.error('Failed to save account template:', error);
															toast.error('Ошибка сохранения шаблона');
														} finally {
															setSavingTemplate(false);
														}
													}}
													disabled={savingTemplate}
													className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
													style={{ backgroundColor: colors.primary }}
													onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.9'; }}
													onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
												>
													{savingTemplate ? 'Сохранение...' : 'Сохранить для аккаунта'}
												</button>
											</div>
										)}
									</div>
								</div>

								{/* Шаблон одобрения */}
								<div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
									<div className="px-4 py-3 space-y-2">
									<div className="flex items-center gap-2">
										<svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<h5 className="text-sm font-semibold text-gray-900">Шаблон одобрения</h5>
									</div>

									{(accountApprovalTemplate || positionApprovalTemplate) && (
										<div className="flex flex-wrap gap-1.5 mb-1">
											{accountApprovalTemplate && (
												<button
													type="button"
													onClick={() => setApprovalTemplate(accountApprovalTemplate)}
													className="px-2 py-0.5 text-[11px] rounded border transition-colors"
													style={{
														borderColor: approvalTemplate === accountApprovalTemplate ? colors.primary : '#e5e7eb',
														color: approvalTemplate === accountApprovalTemplate ? colors.primary : '#6b7280',
														backgroundColor: approvalTemplate === accountApprovalTemplate ? hexToRgba(colors.primary, 0.1) : 'transparent'
													}}
												>
													Шаблон аккаунта
												</button>
											)}
											{positionApprovalTemplate && (
												<button
													type="button"
													onClick={() => setApprovalTemplate(positionApprovalTemplate)}
													className="px-2 py-0.5 text-[11px] rounded border transition-colors"
													style={{
														borderColor: approvalTemplate === positionApprovalTemplate ? '#059669' : '#e5e7eb',
														color: approvalTemplate === positionApprovalTemplate ? '#059669' : '#6b7280',
														backgroundColor: approvalTemplate === positionApprovalTemplate ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
													}}
												>
													Шаблон вакансии
												</button>
											)}
										</div>
									)}

									<textarea
										value={approvalTemplate}
										onChange={(e) => setApprovalTemplate(e.target.value)}
										rows={3}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-vertical"
										style={{ focusRingColor: colors.primary } as any}
										onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.boxShadow = `0 0 0 2px ${hexToRgba(colors.primary, 0.2)}`; }}
										onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
										placeholder="{{candidate_name}}, ваше резюме было одобрено. Рекрутер свяжется с вами в ближайшее время."
									/>
									<div className="flex flex-wrap gap-1.5">
										<button
											type="button"
											onClick={() => setApprovalTemplate(prev => prev + '{{candidate_name}}')}
											className="px-2 py-0.5 text-[11px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
										>
											{'{{candidate_name}}'}
										</button>
									</div>

									{approvalTemplate !== approvalTemplateOriginal && (
										<div className="flex flex-wrap justify-end gap-2 mt-2">
											{(selectedCampaignData?.positionId || selectedVacancyId) && (
												<button
													onClick={async () => {
														const posId = selectedCampaignData?.positionId || selectedVacancyId;
														if (!posId) return;
														setSavingTemplate(true);
														try {
															await savePositionTemplate(posId, { approvalTemplateText: approvalTemplate });
															setApprovalTemplateOriginal(approvalTemplate);
															setPositionApprovalTemplate(approvalTemplate);
															toast.success('Шаблон одобрения вакансии сохранён');
														} catch (error) {
															console.error('Failed to save position approval template:', error);
															toast.error('Ошибка сохранения');
														} finally {
															setSavingTemplate(false);
														}
													}}
													disabled={savingTemplate}
													className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
													style={{ backgroundColor: '#059669' }}
												>
													{savingTemplate ? 'Сохранение...' : 'Сохранить для вакансии'}
												</button>
											)}
											<button
												onClick={async () => {
													setSavingTemplate(true);
													try {
														await saveAccountTemplate({ approvalTemplateText: approvalTemplate });
														setApprovalTemplateOriginal(approvalTemplate);
														setAccountApprovalTemplate(approvalTemplate);
														toast.success('Шаблон одобрения аккаунта сохранён');
													} catch (error) {
														console.error('Failed to save account approval template:', error);
														toast.error('Ошибка сохранения');
													} finally {
														setSavingTemplate(false);
													}
												}}
												disabled={savingTemplate}
												className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
												style={{ backgroundColor: colors.primary }}
											>
												{savingTemplate ? 'Сохранение...' : 'Сохранить для аккаунта'}
											</button>
										</div>
									)}
									</div>
								</div>

								{/* Шаблон отказа */}
								<div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
									<div className="px-4 py-3 space-y-2">
									<div className="flex items-center gap-2">
										<svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										<h5 className="text-sm font-semibold text-gray-900">Шаблон отказа</h5>
									</div>

									{(accountRejectionTemplate || positionRejectionTemplate) && (
										<div className="flex flex-wrap gap-1.5 mb-1">
											{accountRejectionTemplate && (
												<button
													type="button"
													onClick={() => setRejectionTemplate(accountRejectionTemplate)}
													className="px-2 py-0.5 text-[11px] rounded border transition-colors"
													style={{
														borderColor: rejectionTemplate === accountRejectionTemplate ? colors.primary : '#e5e7eb',
														color: rejectionTemplate === accountRejectionTemplate ? colors.primary : '#6b7280',
														backgroundColor: rejectionTemplate === accountRejectionTemplate ? hexToRgba(colors.primary, 0.1) : 'transparent'
													}}
												>
													Шаблон аккаунта
												</button>
											)}
											{positionRejectionTemplate && (
												<button
													type="button"
													onClick={() => setRejectionTemplate(positionRejectionTemplate)}
													className="px-2 py-0.5 text-[11px] rounded border transition-colors"
													style={{
														borderColor: rejectionTemplate === positionRejectionTemplate ? '#059669' : '#e5e7eb',
														color: rejectionTemplate === positionRejectionTemplate ? '#059669' : '#6b7280',
														backgroundColor: rejectionTemplate === positionRejectionTemplate ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
													}}
												>
													Шаблон вакансии
												</button>
											)}
										</div>
									)}

									<textarea
										value={rejectionTemplate}
										onChange={(e) => setRejectionTemplate(e.target.value)}
										rows={3}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-vertical"
										style={{ focusRingColor: colors.primary } as any}
										onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.boxShadow = `0 0 0 2px ${hexToRgba(colors.primary, 0.2)}`; }}
										onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
										placeholder="{{candidate_name}}, к сожалению, по вашему резюме пришел отказ. Но мы в любом случае остаемся на связи, если будет что-то более подходящее, я вам сразу напишу!"
									/>
									<div className="flex flex-wrap gap-1.5">
										<button
											type="button"
											onClick={() => setRejectionTemplate(prev => prev + '{{candidate_name}}')}
											className="px-2 py-0.5 text-[11px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
										>
											{'{{candidate_name}}'}
										</button>
									</div>

									{rejectionTemplate !== rejectionTemplateOriginal && (
										<div className="flex flex-wrap justify-end gap-2 mt-2">
											{(selectedCampaignData?.positionId || selectedVacancyId) && (
												<button
													onClick={async () => {
														const posId = selectedCampaignData?.positionId || selectedVacancyId;
														if (!posId) return;
														setSavingTemplate(true);
														try {
															await savePositionTemplate(posId, { rejectionTemplateText: rejectionTemplate });
															setRejectionTemplateOriginal(rejectionTemplate);
															setPositionRejectionTemplate(rejectionTemplate);
															toast.success('Шаблон отказа вакансии сохранён');
														} catch (error) {
															console.error('Failed to save position rejection template:', error);
															toast.error('Ошибка сохранения');
														} finally {
															setSavingTemplate(false);
														}
													}}
													disabled={savingTemplate}
													className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
													style={{ backgroundColor: '#059669' }}
												>
													{savingTemplate ? 'Сохранение...' : 'Сохранить для вакансии'}
												</button>
											)}
											<button
												onClick={async () => {
													setSavingTemplate(true);
													try {
														await saveAccountTemplate({ rejectionTemplateText: rejectionTemplate });
														setRejectionTemplateOriginal(rejectionTemplate);
														setAccountRejectionTemplate(rejectionTemplate);
														toast.success('Шаблон отказа аккаунта сохранён');
													} catch (error) {
														console.error('Failed to save account rejection template:', error);
														toast.error('Ошибка сохранения');
													} finally {
														setSavingTemplate(false);
													}
												}}
												disabled={savingTemplate}
												className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
												style={{ backgroundColor: colors.primary }}
											>
												{savingTemplate ? 'Сохранение...' : 'Сохранить для аккаунта'}
											</button>
										</div>
									)}
									</div>
								</div>
							</div>

							{/* Требования к диалогу — два списка (Обязательные / Дополнительные).
							   Без весов, рекрутер может добавлять свои. Сохраняется в
							   aiRequirementsWeighted.for_dialog как {required: [...], optional: [...]}.
							   Бот ведёт диалог по обоим, отказывает только на отрицание ОБЯЗАТЕЛЬНОГО. */}
							<div className="bg-white rounded-lg p-6 shadow-sm space-y-4">
								<div className="flex items-center gap-2">
									<h3 className="text-lg font-semibold text-gray-900">Требования к диалогу</h3>
									<span className="text-sm text-gray-500">(желательно не более 5)</span>
								</div>
								<p className="text-xs text-gray-500">
									Бот спросит у кандидата всё, чего нет в резюме. Если нет ОБЯЗАТЕЛЬНОГО — откажет. Если нет ДОПОЛНИТЕЛЬНОГО — продолжит диалог.
								</p>

								{loadingRequirements ? (
									<ul className="space-y-2" aria-label="Загрузка требований">
										{[0, 1, 2].map((i) => (
											<li
												key={i}
												className="relative h-10 rounded-lg bg-gray-100 overflow-hidden"
												style={{ animation: `ai-fade-in 0.3s ease-out ${i * 80}ms both` }}
											>
												<div
													className="absolute inset-0"
													style={{
														background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)',
														animation: 'shimmer 1.2s ease-in-out infinite',
													}}
												/>
											</li>
										))}
										<style>{`@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
									</ul>
								) : (() => {
									const renderRow = (req: FlatReq) => {
										const idx = candidateRequirements.findIndex(r => r.id === req.id);
										const isRemoving = removingReqIds.has(req.id);
										const isEditing = editingReqId === req.id;
										return (
											<li
												key={req.id}
												className={`relative group ai-fade-in transition-all duration-200 ease-out ${
													isRemoving ? 'opacity-0 -translate-x-2 scale-95' : 'opacity-100 translate-x-0 scale-100'
												}`}
											>
												<textarea
													ref={(el) => { if (el) (reqTextareaRefs.current[req.id] = el); else delete reqTextareaRefs.current[req.id]; }}
													value={req.text}
													onChange={(e) => {
														if (idx < 0) return;
														const next = [...candidateRequirements];
														next[idx] = { ...req, text: e.target.value };
														setCandidateRequirements(next);
													}}
													readOnly={!isEditing}
													rows={1}
													style={{ fieldSizing: 'content' } as React.CSSProperties}
													className={`w-full min-h-[2.5rem] pl-3 pr-20 py-2 border rounded-lg text-sm focus:outline-none resize-none overflow-hidden transition-colors ${
														isEditing
															? 'border-gray-300 bg-white cursor-text'
															: 'border-transparent bg-gray-50 hover:bg-gray-100 cursor-default'
													}`}
													onFocus={(e) => {
														if (!isEditing) return;
														e.currentTarget.style.borderColor = colors.primary;
														e.currentTarget.style.boxShadow = `0 0 0 2px ${hexToRgba(colors.primary, 0.2)}`;
													}}
													onBlur={(e) => {
														e.currentTarget.style.borderColor = '';
														e.currentTarget.style.boxShadow = '';
														if (!isEditing) return;
														setEditingReqId(null);
														if (req.text.trim() === '') {
															setRemovingReqIds(prev => new Set(prev).add(req.id));
															setTimeout(() => {
																setCandidateRequirements(cur => cur.filter(r => r.id !== req.id));
																setRemovingReqIds(prev => {
																	const next = new Set(prev);
																	next.delete(req.id);
																	return next;
																});
															}, 200);
														}
													}}
													onKeyDown={(e) => {
														if (e.key === 'Escape') {
															e.currentTarget.blur();
														} else if (e.key === 'Enter' && !e.shiftKey) {
															e.preventDefault();
															e.currentTarget.blur();
														}
													}}
													disabled={isRemoving}
												/>
												<div className={`absolute top-1.5 right-2 flex items-center gap-0.5 transition-opacity duration-150 ${
													isEditing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
												}`}>
													<button
														type="button"
														onClick={() => {
															setEditingReqId(req.id);
															requestAnimationFrame(() => {
																const ta = reqTextareaRefs.current[req.id];
																if (ta) {
																	ta.focus();
																	ta.setSelectionRange(ta.value.length, ta.value.length);
																}
															});
														}}
														disabled={isRemoving}
														className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
														title="Редактировать"
													>
														<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
															<path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
														</svg>
													</button>
													<button
														type="button"
														onMouseDown={(e) => e.preventDefault()}
														onClick={() => {
															setRemovingReqIds(prev => new Set(prev).add(req.id));
															setTimeout(() => {
																setCandidateRequirements(cur => cur.filter(r => r.id !== req.id));
																setRemovingReqIds(prev => {
																	const next = new Set(prev);
																	next.delete(req.id);
																	return next;
																});
															}, 200);
														}}
														disabled={isRemoving}
														className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
														title="Удалить"
													>
														<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
															<path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
														</svg>
													</button>
												</div>
											</li>
										);
									};
									const mandatoryReqs = candidateRequirements.filter(r => r.mandatory);
									const optionalReqs = candidateRequirements.filter(r => !r.mandatory);
									return (
										<>
											{/* Обязательные */}
											<div className="space-y-2">
												<h4 className="text-sm font-medium text-gray-800">Обязательные <span className="text-gray-500">({mandatoryReqs.length})</span></h4>
												{mandatoryReqs.length === 0 ? (
													<div className="text-xs text-gray-400 italic px-1">Пусто</div>
												) : (
													<ul className="space-y-2">{mandatoryReqs.map(renderRow)}</ul>
												)}
											</div>
											{/* Дополнительные */}
											<div className="space-y-2 pt-3 border-t border-gray-100">
												<h4 className="text-sm font-medium text-gray-800">Дополнительные <span className="text-gray-500">({optionalReqs.length})</span></h4>
												{optionalReqs.length === 0 ? (
													<div className="text-xs text-gray-400 italic px-1">Пусто</div>
												) : (
													<ul className="space-y-2">{optionalReqs.map(renderRow)}</ul>
												)}
											</div>
										</>
									);
								})()}

								{/* Add menu — same style as RequirementsSection: один внизу, popup для выбора группы */}
								<div className="relative flex items-center gap-3 mt-2 pt-2 border-t border-[#f0f1f3]">
									<button
										ref={addReqButtonRef}
										type="button"
										onClick={() => setShowAddReqMenu(v => !v)}
										className="flex items-center gap-1 text-[13px] font-medium"
										style={{ color: colors.primary }}
									>
										+ Добавить своё требование
									</button>
									{showAddReqMenu && (
										<div
											ref={addReqMenuRef}
											className="absolute left-0 bottom-full mb-1 bg-white rounded-[10px] border border-[#e2e4e9] shadow-lg py-1 z-10 min-w-[200px] ai-fade-in"
										>
											<button
												type="button"
												onClick={() => { setShowAddReqMenu(false); const newId = `new-m-${Date.now()}`; setCandidateRequirements(prev => [...prev, { id: newId, text: '', mandatory: true }]); setEditingReqId(newId); requestAnimationFrame(() => { const ta = reqTextareaRefs.current[newId]; if (ta) ta.focus(); }); }}
												className="w-full px-3 py-2 text-left text-[13px] text-[#0a0d14] hover:bg-[#f9fafb] transition-colors flex items-center gap-2"
											>
												<span className="w-2 h-2 rounded-full bg-[#E16349]" />
												Обязательное
											</button>
											<button
												type="button"
												onClick={() => { setShowAddReqMenu(false); const newId = `new-o-${Date.now()}`; setCandidateRequirements(prev => [...prev, { id: newId, text: '', mandatory: false }]); setEditingReqId(newId); requestAnimationFrame(() => { const ta = reqTextareaRefs.current[newId]; if (ta) ta.focus(); }); }}
												className="w-full px-3 py-2 text-left text-[13px] text-[#0a0d14] hover:bg-[#f9fafb] transition-colors flex items-center gap-2"
											>
												<span className="w-2 h-2 rounded-full bg-[#375DFB]" />
												Дополнительное
											</button>
										</div>
									)}
								</div>

								<div className="flex items-center justify-end pt-2">{/* нижний ряд только для save/cancel */}

									{(() => {
										const cleaned = candidateRequirements.map(r => ({ ...r, text: r.text.trim() })).filter(r => r.text);
										const original = candidateRequirementsOriginal;
										const isDirty =
											cleaned.length !== original.length ||
											cleaned.some((r, i) => original[i]?.id !== r.id || original[i]?.text !== r.text || original[i]?.mandatory !== r.mandatory);
										if (!isDirty) return null;
										return (
											<div className="flex items-center gap-2">
												<button
													type="button"
													onClick={() => setCandidateRequirements(candidateRequirementsOriginal)}
													disabled={savingRequirements}
													className="px-3 py-1.5 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
												>
													Отмена
												</button>
												<button
													type="button"
													onClick={async () => {
														const posId = selectedCampaignData?.positionId || selectedVacancyId;
														if (!posId) return;
														setSavingRequirements(true);
														try {
															const toSave = candidateRequirements
																.map(r => ({ ...r, text: r.text.trim() }))
																.filter(r => r.text);
															const payload = {
																aiRequirementsWeighted: JSON.stringify({
																	required: Array.isArray((fullRequirementsJsonRef.current || {}).required) ? (fullRequirementsJsonRef.current || {}).required : [],
																	optional: Array.isArray((fullRequirementsJsonRef.current || {}).optional) ? (fullRequirementsJsonRef.current || {}).optional : [],
																	for_dialog: {
																		required: toSave.filter(r => r.mandatory).map(r => r.text),
																		optional: toSave.filter(r => !r.mandatory).map(r => r.text),
																	},
																}),
															} as any;
															await apiService.updatePosition(Number(posId), payload);
																try { fullRequirementsJsonRef.current = JSON.parse((payload as any).aiRequirementsWeighted); } catch {}
															setCandidateRequirements(toSave);
															setCandidateRequirementsOriginal(toSave);
															// Sync Zustand store so the load-effect on remount (back-nav) sees fresh
															// aiRequirementsWeighted instead of the cached pre-save value.
															if (String(selectedVacancyId) === String(posId)) {
																try { await refreshSelectedVacancy(); } catch (e) { console.error('Failed to refresh selectedVacancy after requirements save:', e); }
															}
															toast.success('Требования сохранены');
														} catch (err) {
															console.error('Failed to save requirements:', err);
															toast.error('Ошибка сохранения требований');
														} finally {
															setSavingRequirements(false);
														}
													}}
													disabled={savingRequirements}
													className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors"
													style={{ backgroundColor: colors.primary }}
												>
													{savingRequirements ? 'Сохранение...' : 'Сохранить требования'}
												</button>
											</div>
										);
									})()}
								</div>
							</div>

							{/* Candidates Table */}
							<div className="bg-white rounded-lg p-6 shadow-sm space-y-4">
								<h3 className="text-lg font-semibold text-gray-900">Список кандидатов</h3>

								{loadingCandidates ? (
									<div className="flex items-center justify-center py-8">
										<div className="text-gray-500">Загрузка кандидатов...</div>
									</div>
								) : candidates.length === 0 ? (
									<div className="flex items-center justify-center py-8">
										<p className="text-gray-500">Нет кандидатов для этой рассылки</p>
									</div>
								) : (
									<div className="overflow-x-auto">
										<table className="min-w-full divide-y divide-gray-200">
											<thead className="bg-gray-50">
												<tr>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Имя
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Статус
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Способы связи
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Мессенджер диалога
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														ПДН согласие
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Резюме
													</th>
													<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Дата добавления
													</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{candidates.map((candidate) => {
													const candidateName = getCandidateDisplayName(candidate);
													const communicationMethods = getCandidateCommunicationMethods(candidate);
													const dialogueMessenger = getCandidateDialogueMessenger(candidate);
													const dialogueId = candidate.dialogueId || candidate.conversationId;
													const resumeAction = getCandidateResumeAction(candidate);
													// Format createdAt from Java LocalDateTime array
													let formattedDate = '-';
													if (candidate.createdAt) {
														if (Array.isArray(candidate.createdAt)) {
															const [year, month, day, hour, minute] = candidate.createdAt;
															formattedDate = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
														} else {
															formattedDate = new Date(candidate.createdAt).toLocaleString('ru-RU');
														}
													}

													// Стили статуса — TASKNEIROKLYUCH-771/828: enum синхронизирован с backend,
													// HH-error-code больше не разбивается на отдельные цвета.
													const getStatusStyle = (status: string) => {
														switch (status) {
															case 'SENDING':
																return 'bg-amber-50 text-amber-700';
															case 'CONTACTED':
																return 'bg-blue-100 text-blue-800';
															case 'QUALIFIED':
																return 'bg-green-100 text-green-800';
															case 'REJECTED':
															case 'ERROR':
																return 'bg-red-100 text-red-800';
															case 'SKIPPED':
																return 'bg-gray-100 text-gray-600';
															case 'PENDING':
															default:
																return 'bg-yellow-50 text-yellow-700';
														}
													};

													// TASKNEIROKLYUCH-771/828: enum синхронизирован с backend. В фильтре Диалогов
													// ERROR — единый «Ошибка», но в табличной вьюхе вакансий рекрутеру
													// нужна диагностика HH прямо в строке (см. mapErrorCodeToLabel),
													// already_applied всегда идёт через SKIPPED.
													const getStatusLabel = (
														status: string,
														skipReason?: string | null,
														errorCode?: string | null,
													): string => {
														const labels: Record<string, string> = {
															'PENDING': 'Ожидает',
															'SENDING': 'Отправляется',
															'CONTACTED': 'Контакт установлен',
															'SKIPPED': 'Пропущен',
															'ERROR': 'Ошибка',
															'QUALIFIED': 'Одобрен',
															'REJECTED': 'Отклонён'
														};

														if (status === 'SKIPPED' && skipReason) {
															const reason = skipReason.toLowerCase();
															if (reason.includes('already_applied') && !reason.includes('not_already_applied')) {
																return 'Уже откликался';
															}
														}

														if (status === 'ERROR') {
															const label = mapErrorCodeToLabel(errorCode, skipReason);
															if (label) {
																return label;
															}
														}

														return labels[status] || status;
													};

													return (
														<tr key={candidate.id}>
															<td className="px-6 py-4 whitespace-nowrap">
																<div className="flex items-center gap-2">
																	{candidate.pdnConsent === true && candidate.photoUrl ? (
																		<img
																			src={candidate.photoUrl}
																			alt={candidateName}
																			className="w-6 h-6 rounded-full object-cover bg-gray-200"
																			onError={(e) => {
																				e.currentTarget.onerror = null;
																				e.currentTarget.style.display = 'none';
																				e.currentTarget.nextElementSibling?.classList.remove('hidden');
																			}}
																		/>
																	) : null}
																	<div className={`w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center ${candidate.pdnConsent === true && candidate.photoUrl ? 'hidden' : ''}`}>
																		<svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
																			<path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
																		</svg>
																	</div>
																	<span className="text-sm font-medium text-gray-900">
																		{candidateName}
																	</span>
																</div>
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																{candidate.status === 'ERROR' ? (
																	<button
																		type="button"
																		onClick={() => openRetryCandidateDialog(candidate)}
																		className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors hover:ring-2 hover:ring-red-200 focus:outline-none focus:ring-2 focus:ring-red-300 ${getStatusStyle(candidate.status)}`}
																		title="Повторить отправку сообщения"
																	>
																		{getStatusLabel(candidate.status, candidate.skipReason, candidate.errorCode)}
																	</button>
																) : (
																	<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusStyle(candidate.status)}`}>
																		{getStatusLabel(candidate.status, candidate.skipReason, candidate.errorCode)}
																	</span>
																)}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																{communicationMethods.length > 0 ? (
																	<div className="flex items-center gap-2">
																		{communicationMethods.map((method) => {
																			const meta = CHANNEL_META[method.type];
																			if (!meta) return null;
																			return (
																				<button
																					key={`${method.type}-${method.url}`}
																					type="button"
																					onClick={() => handleOpenCommunicationMethod(method)}
																					aria-label={meta.label}
																					title={meta.label}
																					className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${meta.className}`}
																				>
																					<ChannelIcon type={method.type} />
																				</button>
																			);
																		})}
																	</div>
																) : (
																	<span className="text-gray-400">-</span>
																)}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																{dialogueMessenger === 'HH' && candidate.resumeUrl ? (
																	<a
																		href={candidate.resumeUrl}
																		target="_blank"
																		rel="noopener noreferrer"
																		aria-label="HeadHunter"
																		title="HeadHunter"
																		className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${CHANNEL_META.HH.className}`}
																	>
																		<ChannelIcon type="HH" />
																	</a>
																) : dialogueMessenger && dialogueMessenger !== 'HH' && dialogueId ? (
																	<button
																		type="button"
																		onClick={() => window.open(`/recruiter/messenger?dialogId=${encodeURIComponent(String(dialogueId))}`, '_blank', 'noopener,noreferrer')}
																		aria-label={CHANNEL_META[dialogueMessenger].label}
																		title={CHANNEL_META[dialogueMessenger].label}
																		className={`inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200 transition-colors ${CHANNEL_META[dialogueMessenger].className}`}
																	>
																		<ChannelIcon type={dialogueMessenger} />
																	</button>
																) : (
																	<span className="text-gray-400">-</span>
																)}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${candidate.pdnConsent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
																	{candidate.pdnConsent ? 'Да' : 'Нет'}
																</span>
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm">
																{resumeAction?.type === 'link' ? (
																	<a
																		href={resumeAction.url}
																		target="_blank"
																		rel="noopener noreferrer"
																		title={resumeAction.title}
																		className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full transition-colors ${resumeAction.className}`}
																	>
																		{resumeAction.label}
																	</a>
																) : resumeAction?.type === 'estaff' ? (
																	<button
																		type="button"
																		onClick={() => openEStaffResume(resumeAction.data)}
																		title="Открыть резюме E-Staff"
																		className="inline-flex items-center px-3 py-1 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold rounded-full transition-colors"
																	>
																		E-Staff
																	</button>
																) : (
																	<span className="text-gray-400">-</span>
																)}
															</td>
															<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
																{formattedDate}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</div>

						</>
					)}
				</div>
			</div>

			{/* Confirmation Dialog */}
			<Dialog.Root open={confirmDialog.isOpen} onOpenChange={closeConfirmDialog}>
				{confirmDialog.isOpen && (
					<Dialog.Portal>
						<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
						<Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50">
							<Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.title}</Dialog.Title>
							<Dialog.Description className="text-gray-600 mb-6">{confirmDialog.message}</Dialog.Description>
							<div className="flex gap-3 justify-end">
								<button
									onClick={closeConfirmDialog}
									className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
								>
									Отмена
								</button>
								<button
									onClick={() => {
										confirmDialog.onConfirm();
										closeConfirmDialog();
									}}
									className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
								>
									Подтвердить
								</button>
							</div>
						</Dialog.Content>
					</Dialog.Portal>
				)}
			</Dialog.Root>

			<Dialog.Root open={retryCandidateDialog.isOpen} onOpenChange={(open) => {
				if (!open && !retryCandidateDialog.isSubmitting) {
					closeRetryCandidateDialog();
				}
			}}>
				{retryCandidateDialog.isOpen && (
					<Dialog.Portal>
						<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
						<Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50">
							<Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
								Повторить отправку
							</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-600 mb-4">
								Сообщение кандидату будет поставлено в очередь и отправлено повторно.
							</Dialog.Description>

							{retryCandidateDialog.candidate && (
								<div className="mb-4 text-sm text-gray-800">
									<div className="font-medium text-gray-900">
										{getCandidateDisplayName(retryCandidateDialog.candidate)}
									</div>
								</div>
							)}

							<div className="flex justify-end gap-3">
								<button
									type="button"
									onClick={closeRetryCandidateDialog}
									disabled={retryCandidateDialog.isSubmitting}
									className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-60"
								>
									Отмена
								</button>
								<button
									type="button"
									onClick={handleRetryCandidateMessage}
									disabled={retryCandidateDialog.isSubmitting}
									className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
								>
									{retryCandidateDialog.isSubmitting ? 'Отправляем...' : 'Отправить повторно'}
								</button>
							</div>
						</Dialog.Content>
					</Dialog.Portal>
				)}
			</Dialog.Root>

			<EStaffResumeModal
				open={estaffResumeModalOpen}
				onClose={() => {
					setEstaffResumeModalOpen(false);
					setSelectedEStaffResume(null);
				}}
				resumeData={selectedEStaffResume}
			/>

			{/* Save Progress Dialog */}
			<Dialog.Root open={saveDialog.isOpen} onOpenChange={(open) => {
				// Only allow closing on error status
				if (!open && saveDialog.status === 'error') {
					setSaveDialog({ isOpen: false, status: 'loading', message: '' });
				}
			}}>
				{saveDialog.isOpen && (
					<Dialog.Portal>
						<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
						<Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50">
							<div className="flex flex-col items-center justify-center py-4">
								{saveDialog.status === 'loading' && (
									<>
										<div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 mb-4" style={{ borderBottomColor: colors.primary }}></div>
										<p className="text-lg font-medium text-gray-900">{saveDialog.message}</p>
									</>
								)}
								{saveDialog.status === 'success' && (
									<>
										<div className="mb-4">
											<svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</div>
										<p className="text-lg font-medium text-gray-900">{saveDialog.message}</p>
									</>
								)}
								{saveDialog.status === 'error' && (
									<>
										<div className="mb-4">
											<svg className="w-16 h-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
										</div>
										<p className="text-lg font-medium text-gray-900 mb-6">{saveDialog.message}</p>
										<button
											onClick={() => setSaveDialog({ isOpen: false, status: 'loading', message: '' })}
											className="px-6 py-2 text-white rounded-lg transition-colors"
											style={{ backgroundColor: colors.primary }}
											onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
											onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
										>
											Закрыть
										</button>
									</>
								)}
							</div>
						</Dialog.Content>
					</Dialog.Portal>
				)}
			</Dialog.Root>
		</div>
	);
};

export default VacanciesTab;
